import moment from "moment";

import { RecipientDetailsModel } from "@/Appointments/Models/Appointment/RecipientDetailsModel";
import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { MDClinician } from "@/Common/Api/MedfarDictionaries/Dto/Responses/MDClinician";
import { Pharmacy } from "@/Common/Api/Pharmacy/Dto/Responses/Pharmacy";
import { TimerComponentModel } from "@/Common/Components/Inputs/TimerComponent";
import { AdditionalSignOffInformation } from "@/Common/Components/Modals/LinkingPopup/LinkingPopup";
import { displayToast, MessageToast } from "@/Common/Components/Toast/Toast";
import { CommonHelper } from "@/Common/Helpers/CommonHelper";
import { DateTime } from "@/Common/Helpers/DateTime";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { IAllowlistModel } from "@/Common/Models/Administration/IAllowlistModel";
import { PatientAppointmentItem } from "@/Common/Models/Appointments/PatientAppointmentItem";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { AttachedCommunicationType } from "@/Common/Models/AttachedCommunication/AttachedCommunicationType";
import { EmployeeTitle } from "@/Common/Models/Employee/Enums/EmployeeTitle";
import { MedicalNotePinOption } from "@/Common/Models/Employee/Enums/MedicalNotePinOption";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { FaxLabRequestComposite } from "@/Common/Models/Faxing/FaxLabRequestComposite";
import { FileType } from "@/Common/Models/FileType";
import { Guid } from "@/Common/Models/Guid";
import { MedicalItemsTab, PatientTab } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { SharedItemPriority } from "@/Common/Models/Portal/SharedItem/SharedItemPriority";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { UserSettingsConfig } from "@/Common/Models/Settings/UserSettingsConfig";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { BillingPaymentMethod } from "@/Common/Models/UI/BillingPaymentMethod";
import { PrivateBill_BilledTo } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { integrationBus } from "@/Common/Modularity/IntegrationBus/IntegrationBus";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExternalBillingManager } from "@/Common/Services/ExternalBilling/ExternalBillingManager";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { LuxonDateTimeParser } from "@/Common/Utilities/DateAndTime/LuxonDateTimeParser";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { ExportableFile, IExportableFileLoaded } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFilePopupSettingModel } from "@/ExportableFile/Models/ExportableFilePopupSettingModel";
import { FaxConsultationRequestComposite } from "@/ExportableFile/Models/FaxConsultationRequestComposite";
import { FileTypeAttachment } from "@/ExportableFile/Models/FileTypes/FileTypeAttachment";
import { FileTypeObstetricalFile } from "@/ExportableFile/Models/FileTypes/FileTypeObstetricalFile";
import { FileTypeWorkerClaimCnesst } from "@/ExportableFile/Models/FileTypes/FileTypeWorkerClaimCnesst";
import { FileTypeWorkSafeBcForm } from "@/ExportableFile/Models/FileTypes/FileTypeWorkSafeBcForm";
import { UserSettings } from "@/Globals/Models/UserSettings";
import { ApssClient } from "@/Patient/Api/Apss/ApssClient";
import { FreeFormImage } from "@/Patient/Api/Dto/Responses/FreeFormImage";
import { PatientFormAPSS } from "@/Patient/Api/Dto/Responses/PatientFormAPSS";
import { LightPrivateBill } from "@/Patient/Api/PrivateBill/Dto/LightPrivateBill";
import { ImmunizationModel } from "@/Patient/Components/MedicalItems/Immunization/Models/ImmunizationModel";
import { SyncRvqImmunizationResponse } from "@/Patient/Components/MedicalItems/Immunization/ResponseModels/SyncRvqImmunizationResponse";
import { ObstetricalFormCreatorFactory } from "@/Patient/Components/MedicalNote/ObstetricalFile/FormCreator/ObstetricalFormCreatorFactory";
import { WorkSafeBcFormLight } from "@/Patient/Components/MedicalNote/WorkSafeBc/Models/WorkSafeBcFormLight";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { PatientModelHelper } from "@/Patient/Helpers/PatientModelHelper";
import { RadPdfExportableFileHelper } from "@/Patient/Helpers/RadPdfExportableFileHelper";
import { GetFormsBlockingNoteApprovalRequest, GetFormsToSendOnNoteApprovalRequest } from "@/Patient/IntegrationBus/PatientRequests";
import { DiscountType } from "@/Patient/Models/Billing/Enum/DiscountType";
import { APSSPatientDTO } from "@/Patient/Models/Forms/Apss/APSSPatientDTO";
import { FormDisplay } from "@/Patient/Models/Forms/FormDisplay";
import { PatientForms } from "@/Patient/Models/Forms/RadPdf/Forms";
import { RadPdfForm } from "@/Patient/Models/Forms/RadPdf/RadPdfForm";
import { GenerateFileContext } from "@/Patient/Models/MedicalNote/Enum/GenerateFileContext";
import { MedicalItemSubtypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemSubTypeEnum";
import { MedicalItem, MedicalItemEditContext } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemAlertsAndSpecialNeeds } from "@/Patient/Models/MedicalSummary/MedicalItemAlertsAndAllergies";
import { MedicalItemFamilyHistory } from "@/Patient/Models/MedicalSummary/MedicalItemFamilyHistory";
import { MedicalItemImmunization } from "@/Patient/Models/MedicalSummary/MedicalItemImmunization";
import { MedicalItemMedicalReaction } from "@/Patient/Models/MedicalSummary/MedicalItemMedicalReaction";
import { MedicalItemMedicationAndPrescription } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescription";
import { MedicalItemProblemAndCondition } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { MedicalItemRiskFactor } from "@/Patient/Models/MedicalSummary/MedicalItemRiskFactor";
import { MedicalItemScreeningTest } from "@/Patient/Models/MedicalSummary/MedicalItemScreeningTest";
import { MedicalItemVitalSign } from "@/Patient/Models/MedicalSummary/MedicalItemVitalSign";
import { ObstetricalCounters } from "@/Patient/Models/MedicalSummary/ObstetricalSummary";
import { MedicalItemScreeningTestResponse } from "@/Patient/Models/MedicalSummary/Response/MedicalItemScreeningTestResponse";
import { ObstetricFormContext } from "@/Patient/Models/ObstetricalFile/BaseObstetricalFileForm";
import { ObstetricalFileContributionType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileContributionType";
import { ObstetricalFileType } from "@/Patient/Models/ObstetricalFile/Enum/ObstetricalFileType";
import { ObstetricalFileModel } from "@/Patient/Models/ObstetricalFile/ObstetricalFileModel";
import { PdfFormInboxItem } from "@/Patient/Models/ShareItems/PdfFormInboxItem";
import { BillableServiceDropdownItem } from "@/Patient/Models/UI/BillingHistory/BillableServiceDropdownItem";
import { IBillableService } from "@/Patient/Models/UI/History/IBillableService";
import { MedicalItemViewMode, MedicalItemViewOrigin } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { MedicalNoteViewMode } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalNote";
import { IPatientMedicalNoteModel } from "@/Patient/Models/UI/MedicalNote/IPatientMedicalNoteModel";
import { IMedicalItemsModel } from "@/Patient/Models/UI/MedicalSummary/IMedicalItemsModel";
import { AppointmentStatusChangeEventType, IFaxConsultationRequestComposite, IFaxLabRequestComposite } from "@/Patient/Stores/Actions/PatientMedicalNoteActions";
import { PatientStore } from "@/Patient/Stores/PatientStore";
import { PortalSyncMode } from "@/Portal/Models/PortalSyncMode";
import { sharedItemProviderFactory } from "@/Portal/Services/SharedItemProviderFactory";
import { ConsultationRequestsInboxItem, RequestsInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { MyAPSSFormTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { PatientProfileRequestModalSettingsModel } from "@/Portfolio/Models/PatientProfileRequestModalSettingsModel";

class PatientMedicalNoteHandler extends BaseOperationHandler {
    private readonly unableToShareError = "Unable to share item, We were unable to generate the file.";
    private readonly helper: MyleHelper = new MyleHelper();

    private newNoteDebounce: number;
    private faxDebounceTimeoutReference: number;

    constructor() {
        super();

        // MedicalNote
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ToggleOpenSection, this.toggleSectionExpanded);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ModifyNote, this.modifyNote);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CancelEditNote, this.cancelEditNote);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.SaveEditNote, this.saveEditNote);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.SignoffNote, this.signOffNote);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.SwitchToDetails, this.switchToDetails);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_RemoveClickableTextVisibility, this.removeClickableTextVisibility);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_ShowClickableTextVisibility, this.showClickableTextVisibility);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_ReasonTextChanged, this.reasonTextChanged, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_PresentIllnessHistoryTextChanged, this.presentIllnessHistoryTextChanged, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_ExamTextChanged, this.examTextChanged, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_LaboratoryTextChanged, this.laboratoryTextChanged, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_ImagingTextChanged, this.imagingTextChanged, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_OtherNotesTextChanged, this.otherNotesTextChanged, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_FollowupPlanChanged, this.followupPlanChanged, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ConsultationDetailsPopupShow, this.consultationDetailsPopupShow);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ConsultationDetailsPopupClose, this.consultationDetailsPopupClose);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ConsultationDetailsPopupModify, this.consultationDetailsPopupModify);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CreateRadPdfForm, this.createRadPdf, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.OpenContextMenuPdf, this.openContextMenuPdf);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CloseContextMenuPdf, this.closeContextMenuPdf);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.OpenRadPdfForm, this.openRadPdf);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.RefreshPortalFormStatus, this.updatePortalFormStatus, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.DeletePdfForm, this.deletePdfForm, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ContinueRadPdfForm, this.onContinueRadPdf);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.DuplicateRadPdfForm, this.onDuplicateRadPdfForm);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_NewMedicalHistoryItem, this.newMedicalProblemHistoryItem);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_NewMedicalReactionItem, this.newMedicalReactionItem);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_NewRiskFactorItem, this.newRiskFactorItem);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_NewAlertSpecialNeedItem, this.newAlertItem);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_NewImmunizationItem, this.newImmunizationItem);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_NewActiveMedication, this.newActiveMedicationItem);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_NewVitalSignItem, this.newVitalSignItem);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_NewFamilyHistoryItem, this.newFamilyHistoryItem);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Edit_NewScreeningTestItem, this.newScreeningTestItem);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateUserSettings, this.updateUserSettings);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateUserSettingsExportableFilePopup, this.updateUserSettingsExportableFilePopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateUserSettingsExamRequest, this.updateUserSettingsExamRequest);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateUserSettingsConsultationRequest, this.updateUserSettingsConsultationRequest);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.PrintNote, this.printNote);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.toggleFormPersistance, this.toggleFormPersistance); // set false here
        this.RegisterOperation(LocalPatientOperations.MedicalNote.HandNoteReady, this.handNoteReady, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.DeleteHandNote, this.deleteHandNote, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ConcurrentWarningPopup_NewNote, this.createNewNoteAndSaveEncounter);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ConcurrentWarningPopup_ViewCurrent, this.discardChangedAndViewNote);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.AlreadySignedOffWarningPopup_Ok, this.alreadySignedOffPopupOk);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.LoadVitalsForGraphs, this.loadVitalsForGraphs, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ExpandLeftMenu, this.expandLeftMenu);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CollapseLeftMenu, this.collapseLeftMenu);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.AutoSaveAlreadySignedOffWarningPopup_Ok, this.autoSaveAlreadySignedOffPopupOk, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.SaveScrollPosition, this.saveScrollPosition, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateProgressNote, this.updateProgressNote);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CompleteMedicalNoteLinkedAppointment, this.completeMedicalNoteLinkedAppointment);
        //

        // --- invoice/billing
        this.RegisterOperation(LocalPatientOperations.MedicalNote.PrivateBill_SaveAndFax, this.saveAndFaxPrivateBill);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.PrivateBill_SaveAndPrint, this.saveAndPrintPrivateBill);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ShowPrivateBillPopup, this.showPrivateBillPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ShowPrivateBillEditPopup, this.showPrivateBillEditPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ClosePrivateBillPopup, this.closePrivateBillPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ClosePrivateBillEditPopup, this.closePrivateBillEditPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_LoadDummyServices, this.loadDummyServices);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_OnTodayClick, this.onTodayClick);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_SiteChange, this.onSiteChanged);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_AddNewEmptyService, this.addNewEmptyService);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_RemoveService, this.removeService);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_ChangeHasGST, this.changeHasGst);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_ChangeHasQST, this.changeHasQst);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_ChangeHasHST, this.changeHasHst);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_ChangeHasGenericTax, this.changeHasGenericTax);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_DateOfVisit_Set, this.dateOfVisitChange);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_OnSeletedPaymentChange, this.onSelectedPaymentChange);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_SelectClinician, this.selectClinician);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_RemoveSelectedClinicianChange, this.removeSelectedClinician);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_ClearSelection, this.clearSelection);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_SelectBillableService, this.selectBillableService, false);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_OnPriceChange, this.onPriceChange);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_OnQuantityChange, this.onQuantityChange);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_RemoveService, this.removeService);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_OnDiscountChange, this.onDiscountChange);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_ChangeDiscountType, this.changeDiscountType);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_Save, this.showSaveBillWarning);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_Print, this.printPrivateBill);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.PrivateBillWarning_Cancel, this.closePrivateBillEditPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.PrivateBillWarning_Save, this.savePrivateBill);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.PrivateBillWarning_SaveAndPrint, this.saveAndPrintPrivateBill);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_CloseInvalidate, this.closeInvalidPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_InvalidateChange, this.noteInvalidationChange);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_Invalidate, this.invalidateBill);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_NotesChange, this.billingNotesChange);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_OnCostCenterChange, this.onCostCenterChange);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_SaveInvalidation, this.saveInvalidation);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_OnBilledToChange, this.onBilledToChanged);
        // --------- //

        // REUSING BILL HANDLER (Open Bill Already Existing....)
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ShowReusingBillPopup, this.showReusingBillPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ShowCreatedPrivateBillEditPopup, this.showCreatedPrivateBillEditPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CloseReusingBillPopup, this.closeReusingBillPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ShowReusingPrivateBillCriteria, this.showReusingPrivateBillCriteria);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ReusingPrivateBillFromDateChange, this.reusingPrivateBillFromDateChange);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ReusingPrivateBillToDateChange, this.reusingPrivateBillToDateChange);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ReusingPrivateBillSearchText, this.reusingPrivateBillSearchText);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.SelectReusingPrivateBillClick, this.selectReusingPrivateBillClick);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ReusingContinueClick, this.reusingContinueClick);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_LockProvider, this.lockProvider);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_InvalidNoteChange, this.invalidNoteChange);
        // --------- //

        // Medfar Billing
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.DeleteBill, this.deleteMedfarBill);
        this.RegisterOperation(LocalPatientOperations.MedfarBilling.CloseSpecialityPopup, this.closeMedfarBillingSpecialityPopup);

        // Supervisor
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CloseApprovalSupervisorPopup, this.closeApprovalSupervisorPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.OpenSupervisorSelectionPopup, this.openSupervisorSelectionPopup);

        // APSS
        this.RegisterOperation(LocalPatientOperations.MedicalNote.LoadApssFormInfo, this.loadApssFormInfo);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CreatingAPSSForm, this.creatingApssForm);

        // prescription
        this.RegisterOperation(LocalPatientOperations.Fax.GeneratePrescriptionPDF, this.generatePrescriptionPdf);
        this.RegisterOperation(LocalPatientOperations.Fax.SendPrescriptionFax, this.sendPrescriptionFax);

        // medical note
        this.RegisterOperation(LocalPatientOperations.Fax.GenerateMedicalNotePDF, this.generateMedicalNotePdf);
        this.RegisterOperation(LocalPatientOperations.Fax.SendMedicalNoteFax, this.sendMedicalNoteFax);

        // ConsultRequest Fax
        this.RegisterOperation(LocalPatientOperations.MedicalNote.OpenConsultationRequestSignPopup_ForFax, this.openConsultationRequestSignPopup_ForFax);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CloseConsultationRequest_ForFax, this.closeConsultationRequest_ForFax);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ConsultationRequest_SendFax, this.sendConsultationRequestFax);

        // LabRequests Fax
        this.RegisterOperation(LocalPatientOperations.MedicalNote.OpenLabrequestSignPopup_ForFax, this.openLabrequestSignPopup_ForFax);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CloseLabRequestSignPopup_ForFax, this.closeLabRequestSignPopup_ForFax);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.LabRequest_SendFax, this.sendLabRequestFax);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.LabRequest_CloseFaxResponsePopup, this.closeFaxLabRequestResponse);

        // Update for LabRequest and ConsultRequest
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ConsultationRequestPopup_OnSaveSucces, this.AddConsultationRequestToNote);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.LabRequestPopup_OnSaveSucces, this.AddLabRequestToNote);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ConsultationRequestPopup_ToggleIsInvalid, this.toggleConsultationIsInvalid);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.LabRequestPopup_ToggleIsInvalid, this.toggleRequestIsInvalid);

        // SyncToPortal - LabRequest and ConsultRequest
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Request_PortalClick, this.requestPortalOpen);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Request_ClosePortalPopup, this.closePortalPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Request_SendToPortal, this.sendRequestToPortal);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Request_OnPortalRequestMessageChecked, this.onPortalRequestMessageChecked);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.Request_PortalStatusRevoke, this.onPortalStatusRevoke);

        // FileGeneration
        this.RegisterOperation(LocalPatientOperations.MedicalNote.GenerateConsultationRequestPdf, this.handleGenerateConsultationRequestOperation);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.GenerateLabRequestPdf, this.handleGenerateLabRequestOperation);

        // NOT TOO SURE WHAT THOSE DO... TO ANALYSE
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ClosePinPopup, this.closePinPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ShowUnapproveNoteWarningPopup, this.showUnapproveNoteWarningPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UnapproveNoteOK, this.unapproveNoteOk);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UnapproveNoteCancel, this.unapproveNoteCancel);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ShowDeleteNoteWarningPopup, this.showDeleteNoteWarningPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.DeleteNoteCancel, this.deleteNoteCancel);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.DeleteNoteOK, this.deleteNoteOk);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.CloseDeletedEncounterWarningPopup, this.closeDeletedEncounterWarningPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.LoadAttachedCommunications, this.loadAttachedCommunications);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ShowAttachedCommunicationsList, this.showAttachedCommunicationsList);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.onContainerClick, false);

        this.RegisterOperation(LocalPatientOperations.MedicalNote.MedicalNoteForm_PortalClick, this.openSyncToPortalPopupWithPreview);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.MedicalNoteForm_ClosePortalPopup, this.closePortalPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.MedicalNoteForm_SendToPortal, this.sendMedicalFormToPortal);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.MedicalNoteForm_PortalStatusRevoke, this.onPortalStatusRevoke);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.MedicalNoteForm_OnPortalFormMessageChecked, this.onPortalRequestMessageChecked);

        this.RegisterOperation(LocalPatientOperations.MedicalNote.Request_ClosePortalPopup, this.closeRequestPortalPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.MedicalNoteForm_SetFormToRevoke, this.setFormToRevoke);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateCurrentEncounterExtensionFormsCount, this.updateCurrentEncounterExtensionFormsCount);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateSecondScreenExtensionFormsCount, this.updateSecondScreenExtensionFormsCount);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateImmunizationMedicalItem, this.updateImmunizationMedicalItem);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateImmunizationMedicalItemsAfterRvqSaveAndSync, this.updateImmunizationMedicalItemsAfterRvqSaveAndSync);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.SubmitImmunizationToRvq, this.onSubmitImmunizationToRvq);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.SelectMedicalImmunizationFromRvq, this.selectMedicalImmunizationFromRvq);

        this.RegisterOperation(LocalPatientOperations.MedicalNote.ExternalBilling_ReloadClaims, this.reloadExternalBillingClaimsAsync);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ExternalBilling_IncrementPatientBillingHistoryVersion, this.incrementPatientBillingHistoryReload);

        this.RegisterOperation(LocalPatientOperations.MedicalNote.NewObstetricalFile, this.onNewObstetricalFile);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ObstetricalFileContribution, this.onObstetricalFileContribution);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ShowDeleteObstetricalFileWarningPopup, this.showDeleteObstetricalFileWarningPopup);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.ConfirmDeleteObtetricalFile, this.onConfirmDeleteObstetricalFile);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateObstetricalFileFormDisplay, this.updateObstetricalFileFormDisplay);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.UpdateObstetricalFileForm, this.updateObstetricalFileForm);

        this.RegisterOperation(LocalPatientOperations.MedicalNote.ObstetricalFile_PortalClick, this.openSyncToPortalPopupWithPreview);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.WorkSafeBc_PortalClick, this.openSyncToPortalPopupWithPreview);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.WorkSafeBc_HandleSaveForm, this.handleSaveWorkSafeBcForm);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.WorkSafeBc_UpdateForms, this.updateWorkSafeBcForms);

        this.RegisterOperation(LocalPatientOperations.MedicalNote.Billing_OnRecipientDetailsChange, this.onRecipientDetailsChanged);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.EnableHandWrittenNotesCache, this.enableHandWrittenNotesCache);

        this.RegisterOperation(LocalPatientOperations.MedicalNote.LoadSentFaxes, this.loadSentFaxes);
        this.RegisterOperation(LocalPatientOperations.MedicalNote.SaveNoteActionButtonLog, this.saveNoteActionButtonLog);

        this.newNoteDebounce = -1;
        this.faxDebounceTimeoutReference = -1;
    }

    private creatingApssForm(request: LocalEvent, store: PatientStore): void {
        const code = request.Get<string>("code");
        const patient = request.Get<APSSPatientDTO>("patient");
        let noteModel = patientStore.PatientMedicalNoteModels[request.PatientId];

        store.ActionsMedicalNote.GetEncounterStatus(
            noteModel.get("Encounter").Id,
            "saveEditNote",
            request.PatientId,
            () => {
                ApssClient.createForm(code, patient);
                noteModel = noteModel.set("IsCreatingAPSSForm", true);
                patientStore.PatientMedicalNoteModels[request.PatientId] = noteModel;
                patientStore.EmitChangeToListeners();
            }
        );
    }

    private async loadApssFormInfo(request: LocalEvent, store: PatientStore): Promise<void> {
        const formId = request.Get<number>("formId");
        const historicalId = request.Get<string>("historicalId");

        // Set Is loading
        let model = store.PatientMedicalNoteModels[request.PatientId];
        let forms = model.get("PatientAPSSForms").toArray();
        forms.forEach(x => {
            if (x.APSSId == formId)
                x.IsLoading = true;
        });
        model = model.set("PatientAPSSForms", forms.toImmutable());
        store.PatientMedicalNoteModels[request.PatientId] = model;

        try {
            const remoteForm = await ApssClient.fetchForm(formId, historicalId);
            const tableItem = new MyAPSSFormTableItem(remoteForm);

            model = store.PatientMedicalNoteModels[request.PatientId];
            forms = model.get("PatientAPSSForms").toArray();
            for (let form of forms) {
                if (form.APSSId == formId) {
                    form.APssFormDetails = tableItem;
                    form.IsLoading = false;
                }
            }

            model = model.set("PatientAPSSForms", forms.toImmutable());
            store.PatientMedicalNoteModels[request.PatientId] = model;
            store.EmitChangeToListeners();
        }
        catch (error) {
            console.error("error loading APSS form info", error);
        }
    }

    private toggleSectionExpanded(request: LocalEvent, store: PatientStore): void {
        const section = request.Get<string>("value");
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        switch (section) {
            case "medicalsummary":
                noteModel = noteModel.set("IsMedicalSummarySectionExpanded", !noteModel.get("IsMedicalSummarySectionExpanded"));
                sessionStore.toggleMedicalSummarySection = !noteModel.get("IsMedicalSummarySectionExpanded");
                break;

            case "progressnote":
                noteModel = noteModel.set("IsProgressNoteSectionExpanded", !noteModel.get("IsProgressNoteSectionExpanded"));
                break;
            case "forms":
                noteModel = noteModel.set("IsFormsSectionExpanded", !noteModel.get("IsFormsSectionExpanded"));
                break;
            case "assesment":
                noteModel = noteModel.set("IsAssesmentSectionExpanded", !noteModel.get("IsAssesmentSectionExpanded"));
                break;
            case "plan":
                noteModel = noteModel.set("IsPlanSectionExpanded", !noteModel.get("IsPlanSectionExpanded"));
                break;
            case "clinicalForms":
                noteModel = noteModel.set("IsClinicalFormsExpanded", !noteModel.get("IsClinicalFormsExpanded"));
                break;

            default:
                break;
        }

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private modifyNote(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounterId = noteModel.get("Encounter").Id;
        // Call checkStatus to validate note isnt deleted

        store.ActionsMedicalNote.GetEncounterStatus(
            encounterId,
            "saveEditNote",
            request.PatientId,
            () => {
                // Get Clone For Undo
                store.PatientMedicalNoteUndoModels[request.PatientId] = noteModel.set("ViewMode", noteModel.get("ViewMode"));

                // Set View
                noteModel = noteModel.set("ViewMode", MedicalNoteViewMode.Edit);
                store.PatientMedicalNoteModels[request.PatientId] = noteModel;

                // Set Encounter
                sessionStore.setEncounterTimer(new TimerComponentModel(request.PatientId, noteModel.get("Encounter").Id));
                store.PatientUIModels[request.PatientId] = store.PatientUIModels[request.PatientId].set("EditEncounterIdContext", noteModel.get("Encounter").Id);

                // ------------------------ Followup Actions ----------------------------
                // Manage encounter patient related appointment states
                store.ActionsMedicalNote.ManagePatientAppointmentState(
                    request.PatientId,
                    noteModel.get("Encounter").Id,
                    noteModel.get("Encounter").ConsultationDate,
                    AppointmentStatusChangeEventType.ModifyEncounter
                );

                // Carefull, NoteModel Gets FullyReset In Here
                if (store.PatientMedicalNoteModels[request.PatientId].get("Encounter").EncounterRelatedEmployees.filter(x => x.EmployeeId == sessionStore.getCurrentEmployee().ContactFacetId).length == 0)
                    store.updateContributorsEncountersData(request.PatientId, noteModel.get("Encounter").Id);
            }
        );
    }

    private cancelEditNote(request: LocalEvent, store: PatientStore): void {
        const noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        store.PatientUIModels[request.PatientId] = store.PatientUIModels[request.PatientId].set("EditEncounterIdContext", null);
        sessionStore.setEncounterTimer(null);

        const undoModel = store.PatientMedicalNoteUndoModels[request.PatientId]
            .set("ViewMode", MedicalNoteViewMode.View)
            .set("PatientForms", noteModel.get("PatientForms"))
            .set("ObstetricalFileModel", noteModel.get("ObstetricalFileModel"))
            .set("ObstetricalFileFormDisplay", noteModel.get("ObstetricalFileFormDisplay"))
            .set("WorkSafeBcForms", noteModel.get("WorkSafeBcForms"));

        store.PatientMedicalNoteModels[request.PatientId] = undoModel;
        store.ActionsMedicalNote.LoadEncounterForView(encounter.Id);
        store.PatientMedicalSummaryModels[request.PatientId] = PatientMedicalSummaryHelper.makeEncountersSearchStringsReset(
            store.PatientMedicalSummaryModels[request.PatientId]
        );
    }

    private updateUserSettings(request: LocalEvent, store: PatientStore): void {
        const userSettingsConfig = request.Get<UserSettingsConfig>("userSettingsConfig");
        const noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        if (userSettingsConfig) {
            cacheStore.UserSettings.UserSettingsConfig = userSettingsConfig;
        }

        store.ActionsMedicalNote.LoadEncounterWithoutOpeningView(encounter.Id);
    }

    private updateUserSettingsExportableFilePopup(request: LocalEvent, store: PatientStore): void {
        const settings = ExportableFilePopupSettingModel.toCacheModel(request.Get<ExportableFilePopupSettingModel>("settings"));
        const userSettings = new UserSettings({
            ExportableFilePopup: settings
        });

        if (settings) {
            store.ActionsMedicalNote.saveUserSettings(
                userSettings,
                () => {
                    sessionStore.showPopup({
                        title: cacheStore.GetString("WarningLabel"),
                        content: cacheStore.GetString("DocumentSigningPinOptions_ExportableFilePopupSettingSavingErrorLabel")
                    });
                }
            );
        }
    }

    private updateUserSettingsExamRequest(request: LocalEvent, store: PatientStore): void {
        const settings = PatientProfileRequestModalSettingsModel.toCacheModel(request.Get<PatientProfileRequestModalSettingsModel>("settings"));
        const userSettings = new UserSettings({
            ExamRequestModal: settings
        });

        if (settings) {
            store.ActionsMedicalNote.saveUserSettings(
                userSettings,
                () => {
                    sessionStore.showPopup({
                        title: cacheStore.GetString("WarningLabel"),
                        content: cacheStore.GetString("DocumentSigningPinOptions_ExportableFilePopupSettingSavingErrorLabel")
                    });
                }
            );
        }
    }

    private updateUserSettingsConsultationRequest(request: LocalEvent, store: PatientStore): void {
        const settings = PatientProfileRequestModalSettingsModel.toCacheModel(request.Get<PatientProfileRequestModalSettingsModel>("settings"));
        const userSettings = new UserSettings({
            ConsultationRequestModal: settings
        });

        if (settings) {
            store.ActionsMedicalNote.saveUserSettings(
                userSettings,
                () => {
                    sessionStore.showPopup({
                        title: cacheStore.GetString("WarningLabel"),
                        content: cacheStore.GetString("DocumentSigningPinOptions_ExportableFilePopupSettingSavingErrorLabel")
                    });
                }
            );
        }
    }

    private saveNoteActionButtonLog(request: LocalEvent, store: PatientStore): void {
        const actionType = request.Get<MedicalNotePinOption>("actionType");
        const appointmentId = request.Get<string>("appointmentId");
        const noteModel = patientStore.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");
        const isViewMode = noteModel.get("ViewMode") === MedicalNoteViewMode.View;
        const employeeId = sessionStore.getCurrentEmployee().ContactFacetId;

        store.ActionsMedicalNote.SaveNoteActionButtonLog(encounter.Id, appointmentId, actionType, isViewMode, employeeId);
    }

    private saveEditNote(request: LocalEvent, store: PatientStore): void {
        const then = request.Get<() => void>("then");

        if (this.newNoteDebounce !== -1) {
            clearTimeout(this.newNoteDebounce);
            this.newNoteDebounce = -1;
        }

        this.newNoteDebounce = window.setTimeout(() => {
            const noteModel = patientStore.PatientMedicalNoteModels[request.PatientId];
            const encounter = noteModel.get("Encounter");

            patientStore.PatientUIModels[request.PatientId] = patientStore.PatientUIModels[request.PatientId].set("EditEncounterIdContext", null);
            store.PatientMedicalSummaryModels[request.PatientId] = PatientMedicalSummaryHelper.makeEncountersSearchStringsReset(
                store.PatientMedicalSummaryModels[request.PatientId]
            );
            sessionStore.setEncounterTimer(null);

            // Call checkStatus to validate note isnt signed yet
            store.ActionsMedicalNote.GetEncounterStatus(
                encounter.Id,
                "saveEditNote",
                request.PatientId,
                () => { // If note is NOT Approuved...
                    store.ActionsMedicalNote.SaveEncounter(encounter, then);
                }
            );
        }, 350);
    }

    private async signOffNote(request: LocalEvent, store: PatientStore): Promise<void> {
        const onSuccess = request.Get<() => void>("onSuccess");
        const noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");
        const additionalSignOffInformation = request.Get<AdditionalSignOffInformation>("additionalSignOffInformation");
        store.ActionsMedicalNote.GetEncounterStatus(encounter.Id, "saveEditNote", request.PatientId, async () => {
            // override note speciality if it's not in the employee's specialities
            const employeeSpecialities = sessionStore.getCurrentEmployee().getServiceSpecializations();
            if (employeeSpecialities != null && employeeSpecialities.length > 0 && !employeeSpecialities.some(x => x.LookupID == encounter.ConsultationTypeLookupId))
                encounter.ConsultationTypeLookupId = employeeSpecialities[0].LookupID;

            // disable edit mode
            store.PatientUIModels[request.PatientId] = store.PatientUIModels[request.PatientId].set("EditEncounterIdContext", null);
            sessionStore.setEncounterTimer(null);

            const unsentApssForms = noteModel.get("PatientAPSSForms").filter(form => this.isApssFormUnsent(form)).toArray();

            // sync unsent APSS forms
            for (let form of unsentApssForms) {
                try {
                    const remoteForm = await ApssClient.fetchForm(form.APSSId, form.HistoricalId);
                    form.Status = remoteForm.ReqReg_Etat;
                    form.IsPersistant = true;
                }
                catch (error) {
                    form.IsPersistant = false;
                    sessionStore.showPopup(error);
                }
            }

            // check for unsent forms
            let blockingForms = unsentApssForms
                .filter(form => this.isApssFormUnsent(form)) // update after sync
                .map(form => cacheStore.GetString("APSSLABEL") + ": " + form.FormName);

            const otherBlockingForms = (await integrationBus.fanoutAsync(new GetFormsBlockingNoteApprovalRequest({ patientContactId: request.PatientId }))).flat();
            if (otherBlockingForms.any())
                blockingForms = blockingForms.concat(otherBlockingForms.map(item => item.value));

            if (blockingForms.any()) {
                const sortedBlockingForms = blockingForms.sort((a, b) => a.localeCompare(b));
                sessionStore.showPopup({
                    title: cacheStore.GetString("UnsentEncounterFormsLabel"),
                    content: [cacheStore.GetString("UnsentEncounterFormsWarning")].concat(sortedBlockingForms),
                    contentStyle: "list-after-first"
                });
                return;
            }

            // Show the in progress popup only if any forms will be sent in the same request handler as approving the note.
            // The current note will NOT be approved if any forms trigger an error in the backend.
            // If an error is returned, the error will shown in the console and the progress popup will disappear.
            // Sending the forms will only be done right before the note is set to be approved in the back end.
            const slatedFormsForSendingOnNoteApproval = (await integrationBus.fanoutAsync(new GetFormsToSendOnNoteApprovalRequest({ patientContactId: request.PatientId })))
                .flat()
                .sort((a, b) => a.value.localeCompare(b.value));

            if (slatedFormsForSendingOnNoteApproval?.any() === true) {
                store.PatientUIModels[request.PatientId] = store.PatientUIModels[request.PatientId]
                    .set("IsNoteApprovalInProgress", true)
                    .set("FormsBeingSentOnNoteApproval", slatedFormsForSendingOnNoteApproval);
            }

            // send form
            store.ActionsMedicalNote.SignoffEncounter(
                encounter.Id,
                encounter.ConsultationTypeLookupId,
                additionalSignOffInformation,
                // In case the request returns an error, the following function will close the in progress popup.
                () => {
                    store.PatientUIModels[request.PatientId] = store.PatientUIModels[request.PatientId]
                        .set("IsNoteApprovalInProgress", false)
                        .set("FormsBeingSentOnNoteApproval", []);
                    onSuccess?.();
                }
            );
            store.EmitChangeToListeners();
        });
    }

    private removeClickableTextVisibility(request: LocalEvent, store: PatientStore): void {
        this.hideAllClickableText(request.PatientId, store);
    }

    private showClickableTextVisibility(request: LocalEvent, store: PatientStore): void {
        this.hideAllClickableText(request.PatientId, store);

        const value = request.Get<string>("value");
        let model = store.PatientMedicalNoteModels[request.PatientId];

        switch (value) {
            case "reason":
                model = model.set("ReasonClickableTextVisible", true);
                break;
            case "history":
                model = model.set("HistoryClickableTextVisible", true);
                break;
            case "exam":
                model = model.set("ExamenClickableTextVisible", true);
                break;
            case "lab":
                model = model.set("LabClickableTextVisible", true);
                break;
            case "imaging":
                model = model.set("ImagingClickableTextVisible", true);
                break;
            case "other":
                model = model.set("OtherNotesClickableTextVisible", true);
                break;
            case "followupplan":
                model = model.set("FollowUpPlanClickableTextVisible", true);
                break;
        }
        store.PatientMedicalNoteModels[request.PatientId] = model;
    }

    private hideAllClickableText(patientId: string, store: PatientStore): void {
        const model = store.PatientMedicalNoteModels[patientId]
            .set("ReasonClickableTextVisible", false)
            .set("HistoryClickableTextVisible", false)
            .set("ExamenClickableTextVisible", false)
            .set("LabClickableTextVisible", false)
            .set("ImagingClickableTextVisible", false)
            .set("OtherNotesClickableTextVisible", false)
            .set("FollowUpPlanClickableTextVisible", false);
        store.PatientMedicalNoteModels[patientId] = model;
    }

    private reasonTextChanged(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        encounter.VisitPurpose = request.Get<string>("value");

        noteModel = noteModel
            .delete("Encounter")
            .set("Encounter", encounter);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private presentIllnessHistoryTextChanged(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        encounter.HistoricalNote = request.Get<string>("value");

        noteModel = noteModel
            .delete("Encounter")
            .set("Encounter", encounter);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private examTextChanged(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        encounter.ExamenNote = request.Get<string>("value");

        noteModel = noteModel
            .delete("Encounter")
            .set("Encounter", encounter);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private laboratoryTextChanged(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        encounter.LabNote = request.Get<string>("value");

        noteModel = noteModel
            .delete("Encounter")
            .set("Encounter", encounter);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private imagingTextChanged(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        encounter.RadiologyNote = request.Get<string>("value");

        noteModel = noteModel
            .delete("Encounter")
            .set("Encounter", encounter);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private otherNotesTextChanged(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        encounter.ProgressNote = request.Get<string>("value");

        noteModel = noteModel
            .delete("Encounter")
            .set("Encounter", encounter);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private followupPlanChanged(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        encounter.OtherPlanNote = request.Get<string>("value");

        noteModel = noteModel
            .delete("Encounter")
            .set("Encounter", encounter);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private consultationDetailsPopupShow(request: LocalEvent, store: PatientStore): void {
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("IsEditConsultationDetailsPopupVisible", true);
        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;
        sessionStore.showGlobalOverlay(true);
    }

    private consultationDetailsPopupClose(request: LocalEvent, store: PatientStore): void {
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("IsEditConsultationDetailsPopupVisible", false);
        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;
        sessionStore.hideGlobalOverlay();
    }

    private consultationDetailsPopupModify(request: LocalEvent, store: PatientStore): void {
        const date = request.Get<Date>("consultationDate");
        const consultationType = request.Get<string>("consultationTypeId");
        const consultationContext = request.Get<string>("consultationContextId");
        const consultationSiteId = request.Get<Guid>("consultationSiteId");
        const consultationDepartmentId = request.Get<Guid>("consultationDepartmentId");
        const consultationIsActiveSite = request.Get<boolean>("consultationIsActiveSite");
        const consultationIsActiveDepartment = request.Get<boolean>("consultationIsActiveDepartment");
        const appointmentLinkId = request.Get<string>("appointmentId");
        let patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel.get("Encounter");
        const enableAppointmentLinkSuggestions = request.Get<boolean>("enableAppointmentLinkSuggestions");

        patientNoteModel = patientNoteModel.set("IsModificationLoading", true);
        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;

        store.ActionsMedicalNote.UpdateConsultationDetails(
            encounter.Id,
            date,
            consultationType,
            consultationContext,
            consultationSiteId,
            consultationDepartmentId,
            consultationIsActiveSite,
            consultationIsActiveDepartment,
            appointmentLinkId,
            enableAppointmentLinkSuggestions
        );
    }

    private createRadPdf(request: LocalEvent, store: PatientStore): void {
        const activePatientId = request.PatientId;
        const patientModel = store.PatientModels[activePatientId];
        const employeeModel = cacheStore.GetCurrentEmployee();
        const encounterModel = store.PatientMedicalNoteModels[activePatientId];
        const summaryModel = store.PatientMedicalSummaryModels[activePatientId];
        const empAddressModel = cacheStore.GetSiteById(employeeModel.ActiveSiteId);
        const culture = sessionStore.userLanguageCode;
        const formName = request.Get<string>("form");
        const formId = request.Get<string>("formId");

        store.ActionsMedicalNote.CreatePdfForm(culture, formId, formName, patientModel, employeeModel, encounterModel, summaryModel, empAddressModel);
    }

    private openContextMenuPdf(request: LocalEvent, store: PatientStore): void {
        const activePatientId = request.PatientId;
        const form = request.Get<PatientForms>("form");
        const forms = store.PatientMedicalNoteModels[activePatientId].get("PatientForms");

        const updatedList = forms.map(f => {
            f.contextOpen = f.Id === form.Id;
            return f;
        });
        store.PatientMedicalNoteModels[activePatientId] = store.PatientMedicalNoteModels[activePatientId].set("PatientForms", updatedList.toList());
    }

    private closeContextMenuPdf(request: LocalEvent, store: PatientStore): void {
        const activePatientId = request.PatientId;
        const form = request.Get<PatientForms>("form");

        const forms = store.PatientMedicalNoteModels[activePatientId].get("PatientForms");
        const updatedList = forms.map(f => {
            if (f.Id === form.Id) {
                f.contextOpen = false;
            }
            return f;
        });
        store.PatientMedicalNoteModels[activePatientId] = store.PatientMedicalNoteModels[activePatientId].set("PatientForms", updatedList.toList());
    }

    private deletePdfForm(request: LocalEvent, store: PatientStore): void {
        const activePatientId = request.PatientId;
        const encounterModel = patientStore.PatientMedicalNoteModels[activePatientId];

        store.ActionsMedicalNote.DeleteRadPdf(request.Get<string>("PatientFormId"), encounterModel.get("Encounter").Id);
    }

    private openRadPdf(request: LocalEvent, store: PatientStore): void {
        const employeeModel = cacheStore.GetCurrentEmployee();
        const encounterModel = patientStore.PatientMedicalNoteModels[request.PatientId];
        const isEditMode = request.Get<boolean>("editMode");

        if (isEditMode) {
            let noteModel = store.PatientMedicalNoteModels[request.PatientId];
            store.PatientMedicalNoteModels[request.PatientId] = noteModel.set("IsPatientFormStatusStale", true);
        }

        Client.openRadpdfForm(
            request.Get<string>("patientFormId"),
            employeeModel.ContactFacet_id,
            encounterModel.get("Encounter").Id,
            encounterModel.get("Encounter").ConsultationDate,
            isEditMode,
            null,
            resp => {
                if (resp.EditMode)
                    store.updateContributorsEncountersData(request.PatientId, resp.EncounterId);
            }
        );
    }

    private onContinueRadPdf(request: LocalEvent, store: PatientStore): void {
        const shouldAddFormToRadPdfList = request.Get<boolean>("isFormAddedToEncounter");
        const form = request.Get<RadPdfForm>("form");

        this.setPatientFormStatusAsStale(store, request.PatientId);

        if (shouldAddFormToRadPdfList) {
            this.addNewRadPdfFormToMedicalNote(store, request.PatientId, form);
        }

        store.updateContributorsEncountersData(request.PatientId, form.EncounterId);
    }

    private setPatientFormStatusAsStale(store: PatientStore, patientId: string): void {
        store.PatientMedicalNoteModels[patientId] = store.PatientMedicalNoteModels[patientId]
            .set("IsPatientFormStatusStale", true);
    }

    private addNewRadPdfFormToMedicalNote(store: PatientStore, patientId: string, form: RadPdfForm): void {
        const noteModel = store.PatientMedicalNoteModels[patientId];

        const newPatientFormList = noteModel.get("PatientForms").push(PdfFormInboxItem.fromRadPdfForm(form));

        store.PatientMedicalNoteModels[patientId] = noteModel.set("PatientForms", newPatientFormList);
    }

    private onDuplicateRadPdfForm(request: LocalEvent, store: PatientStore): void {
        const form = request.Get<RadPdfForm>("form");

        this.setPatientFormStatusAsStale(store, request.PatientId);

        this.addNewRadPdfFormToMedicalNote(store, request.PatientId, form);

        store.updateContributorsEncountersData(request.PatientId, form.EncounterId);
    }

    private toggleFormPersistance(request: LocalEvent, store: PatientStore): void {
        const form = request.Get<PatientForms>("form");

        store.ActionsMedicalNote.PatientFormIsPersistant(form.EncounterId, form.Id, !form.IsPersistant);
    }

    private printNote(request: LocalEvent, store: PatientStore): void {
        const encounterId = request.Get<string>("encounterId");
        store.ActionsMedicalNote.GenerateNotePDF(encounterId, false, request.PatientId, true);
    }

    private handNoteReady(request: LocalEvent, store: PatientStore): void {
        const encounterId = request.Get<string>("encounterId");
        const imageUrl = request.Get<string>("imageUrl");
        const editId = request.Get<string>("edit");
        const imageId = request.Get<string>("imageId");

        const noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const handwrittenNotesList: FreeFormImage[] = noteModel.get("HandwrittenNotes").toArray();
        const image: FreeFormImage = handwrittenNotesList.filter(Note => Note.Id === imageId).firstOrDefault();

        let position: number;
        if (image && image.Position)
            position = image.Position;
        else if (!editId && handwrittenNotesList.length === 0)
            position = 0;
        else if (!editId || !image.Position) {
            let maxPosition = Math.max.apply(null, handwrittenNotesList.map(note => note.Position ? note.Position : handwrittenNotesList.length));
            maxPosition++;
            position = maxPosition;
        }

        store.PatientMedicalNoteModels[request.PatientId] = noteModel
            .set("IsHandWrittenNoteModified", true)
            .set("HandWrittenNoteVersion", GuidHelper.NewGuid());

        store.ActionsMedicalNote.SaveFreeFormImage(encounterId, imageUrl, position, editId);
    }

    private deleteHandNote(request: LocalEvent, store: PatientStore): void {
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel.get("Encounter");
        const image = request.Get<FreeFormImage>("note");
        store.ActionsMedicalNote.DeleteFreeFormImage(encounter.Id, image.Id);
    }

    private discardChangedAndViewNote(request: LocalEvent, store: PatientStore): void {
        let noteModel = patientStore.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        noteModel = noteModel
            .set("ConcurrentEditSignedOffBy", "")
            .set("ConcurrentEditSignedOffDate", null)
            .set("ConcurrentEditShowWarningPopup", false);
        patientStore.PatientMedicalNoteModels[request.PatientId] = noteModel;
        store.ActionsMedicalNote.LoadEncounterForView(encounter.Id);

        sessionStore.hideGlobalOverlay();
    }

    private switchToDetailsImpl(viewMode: MedicalItemsTab, activePatientID: string, isFromAssessment: boolean = false, selectedEmrTemplateId: string = null, newItem: MedicalItem = null, isNewItemFromNote: boolean = false): void {
        let patientUi = patientStore.PatientUIModels[activePatientID];
        const medicalNoteModel = patientStore.PatientMedicalNoteModels[activePatientID];
        const medicalSummaryModel = patientStore.PatientMedicalSummaryModels[activePatientID];
        let medicalItemsModel: IMedicalItemsModel = Immutable.fromJS(PatientModelHelper.getDefaultPatientMedicalItemsModel());
        const encounter = medicalNoteModel?.get("Encounter");
        const encounterId = encounter?.Id;

        medicalItemsModel = medicalItemsModel
            .set("IsSourceEncounterSignedOff", !!encounter?.IsSignedOff)
            .set("Encounter", encounter)
            .set("ItemView", viewMode);

        patientUi = patientUi
            .set("PatientMedicalItemsActiveTab", viewMode)
            .set("ActiveVerticalTab", PatientTab.MedicalItems);

        // setup if we are editing a medical problem from assessment section or from medical summary section
        // ( because different save logic must be applied)
        medicalItemsModel = medicalItemsModel.set("IsEditingFromAssessment", isFromAssessment);

        // setup the medical item view in edit mode when we are creating a new medical item
        if (isNewItemFromNote) {
            medicalItemsModel = medicalItemsModel
                .set("IsSourceEncounterSignedOff", false)
                .set("NavigationOrigin", MedicalItemViewOrigin.Note)
                .set("ViewMode", MedicalItemViewMode.Edit);
            if (newItem != null)
                medicalItemsModel = medicalItemsModel.set("SelectedEMRTemplateId", newItem.EMRTemplateId);
        }

        switch (viewMode) {
            case MedicalItemsTab.MedicalProblem:
                let allMedicalProblemsRef: MedicalItemProblemAndCondition[] = [];
                allMedicalProblemsRef = allMedicalProblemsRef
                    .concat(medicalSummaryModel.get("MedicalItemsProblemsOnGoing").toArray())
                    .concat(medicalSummaryModel.get("MedicalItemsProblemsHistory").toArray())
                    .concat(medicalSummaryModel.get("MedicalItemsSurgeryHistory").toArray())
                    .concat(medicalSummaryModel.get("MedicalItemsPsychiatricHistory").toArray())
                    .concat(medicalSummaryModel.get("MedicalItemsGynecologicalHistory").toArray());

                const allMedicalProblems = allMedicalProblemsRef.map(x => MyleHelper.CloneObject(x));

                if (isNewItemFromNote && newItem)
                    allMedicalProblems.push(newItem as MedicalItemProblemAndCondition);

                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(allMedicalProblems));
                medicalItemsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(medicalItemsModel, null, MedicalItemViewOrigin.Note, medicalSummaryModel, encounterId, patientStore.ActionsMedicalSummary.LoadEMRTemplateComments);

                patientStore.PatientMedicalItemsModels[activePatientID] = medicalItemsModel;
                break;

            case MedicalItemsTab.MedicalReactions:

                const allMedicalReactionsRef: MedicalItemMedicalReaction[] = medicalSummaryModel.get("MedicalItemsMedicalReaction").toArray();
                const allMedicalReactions = allMedicalReactionsRef.map(x => MyleHelper.CloneObject(x));

                if (isNewItemFromNote && newItem)
                    allMedicalReactions.push(newItem as MedicalItemMedicalReaction);

                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(allMedicalReactions));
                medicalItemsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(medicalItemsModel, null, MedicalItemViewOrigin.Note, medicalSummaryModel, encounterId, patientStore.ActionsMedicalSummary.LoadEMRTemplateComments);
                patientStore.PatientMedicalItemsModels[activePatientID] = medicalItemsModel;
                break;

            case MedicalItemsTab.RiskFactors:

                const allRiskFactorsRef: MedicalItemRiskFactor[] = medicalSummaryModel.get("MedicalItemsRiskFactor").toArray();
                const allRiskFactors = allRiskFactorsRef.map(x => MyleHelper.CloneObject(x));

                if (isNewItemFromNote && newItem)
                    allRiskFactors.push(newItem as MedicalItemRiskFactor);

                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(allRiskFactors));
                medicalItemsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(medicalItemsModel, null, MedicalItemViewOrigin.Note, medicalSummaryModel, encounterId, patientStore.ActionsMedicalSummary.LoadEMRTemplateComments);
                patientStore.PatientMedicalItemsModels[activePatientID] = medicalItemsModel;
                break;

            case MedicalItemsTab.VitalSigns:
                const allVitalSignsRef: MedicalItemVitalSign[] = medicalSummaryModel.get("MedicalItemsVitalSign").toArray();
                const allVitalSigns = allVitalSignsRef.map(x => MyleHelper.CloneObject(x));

                if (isNewItemFromNote && newItem)
                    allVitalSigns.push(newItem as MedicalItemVitalSign);
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(allVitalSigns));
                medicalItemsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(medicalItemsModel, null, MedicalItemViewOrigin.Note, medicalSummaryModel, encounterId, patientStore.ActionsMedicalSummary.LoadEMRTemplateComments);
                patientStore.PatientMedicalItemsModels[activePatientID] = medicalItemsModel;
                break;

            case MedicalItemsTab.Immunization:
                const allImmunizationsRef: MedicalItemImmunization[] = medicalSummaryModel.get("MedicalItemsImmunization").toArray();
                const allImmunizations = allImmunizationsRef.map(x => MyleHelper.CloneObject(x));

                if (isNewItemFromNote && newItem)
                    allImmunizations.push(newItem as MedicalItemImmunization);
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(allImmunizations));
                medicalItemsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(medicalItemsModel, null, MedicalItemViewOrigin.Note, medicalSummaryModel, encounterId, patientStore.ActionsMedicalSummary.LoadEMRTemplateComments);
                patientStore.PatientMedicalItemsModels[activePatientID] = medicalItemsModel;
                break;

            case MedicalItemsTab.FamilyHistory:
                const allFamilyHistoryRef: MedicalItemFamilyHistory[] = medicalSummaryModel.get("MedicalItemsFamilyHistory").toArray();
                const allFamilyHistory = allFamilyHistoryRef.map(x => MyleHelper.CloneObject(x));

                if (isNewItemFromNote && newItem)
                    allFamilyHistory.push(newItem as MedicalItemFamilyHistory);
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(allFamilyHistory));
                medicalItemsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(medicalItemsModel, null, MedicalItemViewOrigin.Note, medicalSummaryModel, encounterId, patientStore.ActionsMedicalSummary.LoadEMRTemplateComments);
                patientStore.PatientMedicalItemsModels[activePatientID] = medicalItemsModel;
                break;

            case MedicalItemsTab.Alerts:
                const allAlertsRef: MedicalItemAlertsAndSpecialNeeds[] = medicalSummaryModel.get("MedicalItemsAlertAndSpecialNeed").toArray();
                const allAlerts = allAlertsRef.map(x => MyleHelper.CloneObject(x));

                if (isNewItemFromNote && newItem)
                    allAlerts.push(newItem as MedicalItemAlertsAndSpecialNeeds);
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(allAlerts));
                medicalItemsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(medicalItemsModel, null, MedicalItemViewOrigin.Note, medicalSummaryModel, encounterId, patientStore.ActionsMedicalSummary.LoadEMRTemplateComments);
                patientStore.PatientMedicalItemsModels[activePatientID] = medicalItemsModel;
                break;

            case MedicalItemsTab.Medication_Edit:
                const activeMedicationRef: MedicalItemMedicationAndPrescription[] = medicalSummaryModel.get("MedicalItemsActiveMedication").toArray();
                const activeMedication = activeMedicationRef.map(x => {
                    const clonedMed = MyleHelper.CloneObject(x);
                    clonedMed.Dosages = Immutable.List(x.Dosages);
                    return clonedMed;
                });

                activeMedication.push(newItem as MedicalItemMedicationAndPrescription);
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(activeMedication));
                medicalItemsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(medicalItemsModel, null, MedicalItemViewOrigin.Note, medicalSummaryModel, encounterId, patientStore.ActionsMedicalSummary.LoadEMRTemplateComments);
                patientStore.PatientMedicalItemsModels[activePatientID] = medicalItemsModel;
                break;

            case MedicalItemsTab.ScreeningTest:
                const screeningTestsRef: MedicalItemScreeningTestResponse[] = medicalSummaryModel.get("MedicalItemsScreeningTest").toArray();
                const allScreeningTests = screeningTestsRef.map(x => MyleHelper.CloneObject(x));
                if (isNewItemFromNote && newItem)
                    allScreeningTests.push(newItem as MedicalItemScreeningTestResponse);
                medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(allScreeningTests));
                medicalItemsModel = PatientMedicalSummaryHelper.SetMedicalDetailsModelInfo(medicalItemsModel, null, MedicalItemViewOrigin.Note, medicalSummaryModel, encounterId, patientStore.ActionsMedicalSummary.LoadEMRTemplateComments);
                patientStore.PatientMedicalItemsModels[activePatientID] = medicalItemsModel;
                break;

            default:
                break;
        }

        // every time we switch view between medical items && note, we clear medical items state cache for this patient
        this.clearMedicalStateCache(activePatientID);

        patientStore.PatientMedicalNoteModels[activePatientID] = medicalNoteModel;
        patientStore.PatientUIModels[activePatientID] = patientUi;

        if (selectedEmrTemplateId) {
            sessionStore.dispatchAsync(LocalPatientOperations.MedicalItems.SelectEMRTemplate, {
                value: selectedEmrTemplateId
            });
        }
    }

    private switchToDetails(request: LocalEvent): void {
        this.switchToDetailsImpl(request.Get<MedicalItemsTab>("viewMode"), request.PatientId, request.Get<boolean>("isFromAssessment"), request.Get<string>("selectedEMRTemplateId"));
    }

    private clearMedicalStateCache(patientId: string): void {
        delete patientStore.MedicalItemsStateCache[patientId];
    }

    private createNewNoteAndSaveEncounter(request: LocalEvent, store: PatientStore): void {
        let noteModel = patientStore.PatientMedicalNoteModels[request.PatientId];
        const medicalItemsModel = patientStore.PatientMedicalItemsModels[request.PatientId];
        const patientModel = patientStore.PatientModels[request.PatientId];
        const encounter = noteModel.get("Encounter");

        const userConcurencyDetectedDuringMedicalItemSave = noteModel.get("ConcurrenEditIsForMedicalItem");

        noteModel = noteModel
            .set("ConcurrentEditSignedOffBy", "")
            .set("ConcurrentEditSignedOffDate", null)
            .set("ConcurrentEditShowWarningPopup", false)
            .set("ConcurrenEditIsForMedicalItem", false);
        patientStore.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (userConcurencyDetectedDuringMedicalItemSave) {
            const latestMedicalItemId = PatientMedicalItemsHelper.getLatestMedicalItemIdForEMRTemplate(medicalItemsModel.get("MedicalItemsList").toArray());
            store.ActionsMedicalItems.SaveMedicalItemInNewEncounter(medicalItemsModel, latestMedicalItemId, patientModel, noteModel, request.PatientId);
        }
        else
            store.ActionsMedicalNote.SaveAsNewEncounter(encounter);

        sessionStore.hideGlobalOverlay();
    }

    private newMedicalProblemHistoryItem(request: LocalEvent, store: PatientStore): void {
        const editContext = request.Get<MedicalItemEditContext>("editContext");
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];

        const encounter = patientNoteModel?.get("Encounter");
        const isFromAssessment = request.Get<boolean>("isFromAssessment");

        const newItem = PatientMedicalItemsHelper.GetEmptyMedicalProblemItem(encounter?.Id);

        // when adding diagnosis from medical summary in note, set visible in CPP by default
        if (!isFromAssessment)
            newItem.EMRTemplateIsVisibleInCPP = true;
        else
            newItem.EMRTemplateIsVisibleInCPP = settingsStore.configuration.get("AreDiagnosticsInPatientSummary") ? settingsStore.configuration.get("AreDiagnosticsInPatientSummary") : false;

        this.updateMedicalItemEditContext(store, editContext);
        this.switchToDetailsImpl(MedicalItemsTab.MedicalProblem, request.PatientId, isFromAssessment, newItem.EMRTemplateId, newItem, true);
    }

    private newMedicalReactionItem(request: LocalEvent, store: PatientStore): void {
        const editContext = request.Get<MedicalItemEditContext>("editContext");
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel?.get("Encounter");

        const newItem = PatientMedicalItemsHelper.GetEmptyMedicalReactionItem(encounter?.Id);

        this.updateMedicalItemEditContext(store, editContext);
        this.switchToDetailsImpl(MedicalItemsTab.MedicalReactions, request.PatientId, false, newItem.EMRTemplateId, newItem, true);
    }

    private newRiskFactorItem(request: LocalEvent, store: PatientStore): void {
        const editContext = request.Get<MedicalItemEditContext>("editContext");
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel?.get("Encounter");

        const newItem = PatientMedicalItemsHelper.GetEmptyRiskFactorItem(encounter?.Id);

        this.updateMedicalItemEditContext(store, editContext);
        this.switchToDetailsImpl(MedicalItemsTab.RiskFactors, request.PatientId, false, newItem.EMRTemplateId, newItem, true);
    }

    private newAlertItem(request: LocalEvent, store: PatientStore): void {
        const editContext = request.Get<MedicalItemEditContext>("editContext");
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel?.get("Encounter");

        const newItem = PatientMedicalItemsHelper.GetEmptyAlertItem(encounter?.Id);

        this.updateMedicalItemEditContext(store, editContext);
        this.switchToDetailsImpl(MedicalItemsTab.Alerts, request.PatientId, false, newItem.EMRTemplateId, newItem, true);
    }

    private newFamilyHistoryItem(request: LocalEvent, store: PatientStore): void {
        const editContext = request.Get<MedicalItemEditContext>("editContext");
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel?.get("Encounter");

        const newItem = PatientMedicalItemsHelper.GetEmptyFamilyHistoryItem(encounter?.Id);

        this.updateMedicalItemEditContext(store, editContext);
        this.switchToDetailsImpl(MedicalItemsTab.FamilyHistory, request.PatientId, false, newItem.EMRTemplateId, newItem, true);
    }

    private newImmunizationItem(request: LocalEvent, store: PatientStore): void {
        const editContext = request.Get<MedicalItemEditContext>("editContext");
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel?.get("Encounter");

        const newItem = PatientMedicalItemsHelper.GetEmptyImmunizationItem(encounter?.Id);

        this.updateMedicalItemEditContext(store, editContext);
        this.switchToDetailsImpl(MedicalItemsTab.Immunization, request.PatientId, false, newItem.EMRTemplateId, newItem, true);
    }

    private newActiveMedicationItem(request: LocalEvent, store: PatientStore): void {
        const editContext = request.Get<MedicalItemEditContext>("editContext");
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel?.get("Encounter");

        const newItem = PatientMedicalItemsHelper.GetEmptyMedicalItemActiveMedication(encounter?.Id);

        this.updateMedicalItemEditContext(store, editContext);
        this.switchToDetailsImpl(MedicalItemsTab.Medication_Edit, request.PatientId, false, newItem.EMRTemplateId, newItem, true);
    }

    private newVitalSignItem(request: LocalEvent, store: PatientStore): void {
        const editContext = request.Get<MedicalItemEditContext>("editContext");
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel?.get("Encounter");

        const newItem = PatientMedicalItemsHelper.GetEmptyVitalSignItem(encounter?.Id);

        this.updateMedicalItemEditContext(store, editContext);
        this.switchToDetailsImpl(MedicalItemsTab.VitalSigns, request.PatientId, false, newItem.EMRTemplateId, newItem, true);
    }

    private newScreeningTestItem(request: LocalEvent, store: PatientStore): void {
        const editContext = request.Get<MedicalItemEditContext>("editContext");
        const selectedMedicalProblemSubType = request.Get<MedicalItemSubtypeEnum>("selectedMedicalProblemSubType");
        const isMedicalItemFromOutsideNote = request.Get<boolean>("isMedicalItemFromOutsideNote");
        const medicalItemPreviousRoute = request.Get<LocalPatientOperations.MedicalNote>("medicalItemPreviousRoute");
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = patientNoteModel?.get("Encounter");

        store.SelectedMedicalProblemSubType = selectedMedicalProblemSubType;
        store.IsMedicalItemFromOutsideNote = isMedicalItemFromOutsideNote;
        store.MedicalItemPreviousRoute = medicalItemPreviousRoute;

        const newItem = MedicalItemScreeningTestResponse.getEmptyScreeningTest(encounter?.Id);

        this.updateMedicalItemEditContext(store, editContext);
        this.switchToDetailsImpl(MedicalItemsTab.ScreeningTest, request.PatientId, false, newItem.EMRTemplateId, newItem, true);
    }

    private updateMedicalItemEditContext(store: PatientStore, editContext?: MedicalItemEditContext): void {
        if (editContext) {
            store.SelectedMedicalProblemSubType = editContext.selectedMedicalProblemSubType;
            store.IsMedicalItemFromOutsideNote = editContext.isMedicalItemFromOutsideNote;
            store.MedicalItemPreviousRoute = editContext.medicalItemPreviousRoute;
            store.IsCreatingNewMedicalItem = editContext.isCreatingNewMedicalItem;
        }
    }

    private alreadySignedOffPopupOk(request: LocalEvent, store: PatientStore): void {
        let noteModel = patientStore.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");
        // show error notification popup
        noteModel = noteModel
            .set("AlreadySignedOffShowWarningPopup", false)
            .set("AlreadySignedOffBy", "")
            .set("AlreadySignedOffDate", null)
            .set("ConcurrenEditIsForMedicalItem", null);
        sessionStore.hideGlobalOverlay();
        patientStore.PatientMedicalNoteModels[request.PatientId] = noteModel;

        store.ActionsMedicalNote.LoadEncounterForView(encounter.Id);
    }

    private autoSaveAlreadySignedOffPopupOk(request: LocalEvent): void {
        const noteModel = patientStore.PatientMedicalNoteModels[request.PatientId]
            .set("AutosaveFailed", false);
        sessionStore.hideGlobalOverlay();
        patientStore.PatientMedicalNoteModels[request.PatientId] = noteModel;
        patientStore.EmitChangeToListeners();
    }

    private loadVitalsForGraphs(request: LocalEvent): void {
        patientStore.ActionsMedicalItems.LoadVitalSigns(request.PatientId);
    }

    private expandLeftMenu(request: LocalEvent, store: PatientStore): void {
        let model = store.PatientMedicalNoteModels[request.PatientId];
        if (model != null) {
            model = model.set("LeftMenuOpen", true);
            store.PatientMedicalNoteModels[request.PatientId] = model;
        }
    }

    private collapseLeftMenu(request: LocalEvent, store: PatientStore): void {
        let model = store.PatientMedicalNoteModels[request.PatientId];
        if (model != null) {
            model = model.set("LeftMenuOpen", false);
            store.PatientMedicalNoteModels[request.PatientId] = model;
        }
    }

    private saveScrollPosition(request: LocalEvent, store: PatientStore): void {
        const value = request.Get<number>("value");
        const model = store.PatientMedicalNoteModels[request.PatientId]
            .set("ScrollPosition", value);
        store.PatientMedicalNoteModels[request.PatientId] = model;
    }

    // --- invoice/billing
    private showPrivateBillPopup(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const data = request.Get<LightPrivateBill>("privateBill");
        let privateBillModel = Immutable.fromJS(PatientModelHelper.getDefaultPrivateBillModel());

        const createdById = data.CreatedById;
        const creator = cacheStore.GetCliniciansPersonsByContactFacetId(createdById);
        const employeeId = data.ProviderId;
        const employee = cacheStore.GetCliniciansPersonsByContactFacetId(employeeId);

        privateBillModel = privateBillModel
            .set("IsSave", true)
            .set("IsClosed", true);
        if (privateBillModel.get("IsClosed") == true && data.IsInvalid === false) {
            privateBillModel = privateBillModel
                .set("CanInvalidateBill", true);
        }
        privateBillModel = privateBillModel
            .set("ShowDetailsOrNewBill", true)
            .set("IsNewBill", false)
            .set("PrivateBillId", data.PrivateBillId)
            .set("CreatedBy", creator.NameWithPrefix);

        const dateStart = moment(data.BillDate).format("dddd, MMMM DD, YYYY");
        const timeStart = moment(data.BillDate).format("HH:mm:ss");
        const billDateForHeader = dateStart + " " + cacheStore.GetString("AtLabel") + " " + timeStart;

        privateBillModel = privateBillModel
            .set("BillDateForHeader", billDateForHeader)
            .set("BillNumber", data.Number.toString())
            .set("IsInvalid", data.IsInvalid)
            .set("IsFromNote", true);

        if (employee?.NameWithPrefix) {
            privateBillModel = privateBillModel.set("Provider", employee.NameWithPrefix);
        }

        noteModel = noteModel
            .set("PrivateBillPopupModel", privateBillModel)
            .set("IsPrivateBillPopupOpen", true);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        sessionStore.showGlobalOverlay(true);

        store.ActionsMedicalNote.LoadPrivateBillInfo(data.PrivateBillId);
    }

    private showPrivateBillEditPopup(request: LocalEvent, store: PatientStore): void {
        const createdById = sessionStore.getCurrentEmployee().ContactFacetId;
        const creator = cacheStore.GetCliniciansPersonsByContactFacetId(createdById);
        const dateStart = moment().format("dddd, MMMM DD, YYYY");
        const timeStart = moment().format("HH:mm:ss");
        const billDateForHeader = dateStart + " " + cacheStore.GetString("AtLabel") + " " + timeStart;

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        const oldModel = noteModel.get("PrivateBillPopupModel");
        let privateBillModel = oldModel
            .set("SelectedDateOfVisit", DateTime.nowDate())
            .set("SearchText", oldModel.get("SearchText"))
            .set("CancelPopupIsOpen", false)
            .set("IsSave", true)
            .set("ShowDetailsOrNewBill", true)
            .set("IsNewBill", true)
            .set("IsEditBill", false)
            .set("PrivateBillId", null)
            .set("CreatedBy", creator.NameWithPrefix)
            .set("BillDateForHeader", billDateForHeader)
            .set("Provider", creator.NameWithPrefix)
            .set("BillNumber", "0")
            .set("IsInvalid", false)
            .set("SelectedBillRecipientDetails", new RecipientDetailsModel())
            .set("SelectedBillBilledTo", PrivateBill_BilledTo.Patient)
            .set("NewBillNotes", "")
            .set("Notes", "");

        if (sessionStore.rememberPrivateBillProvider) {
            const ps = privateBillModel.get("Clinicians").toArray().filter(c => c.ContactFacetId === sessionStore.privateBillProviderContactFacetId);
            if (ps.length > 0)
                privateBillModel = privateBillModel.set("SelectedClinicians", ps.toImmutable());

            privateBillModel = privateBillModel.set("IsProviderLocked", true);
        }
        else if (sessionStore.myleEmployeeType === 2) {
            const currentEmployee = sessionStore.getCurrentEmployee().Model;
            const isASecretary = Employee.getTitle(sessionStore.getCurrentEmployee().Model) == EmployeeTitle.MedicalOfficeAssistant;
            if (isASecretary && cacheStore.ClinicSettings.EnablePrivateBillRecipient)
                privateBillModel = privateBillModel.set("SelectedClinicians", null);
            else {
                privateBillModel = privateBillModel
                    .set("SelectedClinicians", [Person.FromEmployee(currentEmployee)].toImmutable())
                    .set("SelectedClinicianName", sessionStore.getCurrentEmployee().Name);
            }
        }
        else
            privateBillModel = privateBillModel.set("SelectedClinicians", null);

        privateBillModel = privateBillModel
            .set("DateOfVisit", DateTime.nowDeprecated())
            .set("NewBillTotalAmount", 0)
            .set("NewBillDiscount", 0)
            .set("NewBillTotal", 0)
            .set("NewBillSubtotal", 0)
            .set("NewBillQSTTotal", 0)
            .set("NewBillGSTTotal", 0)
            .set("NewBillHSTTotal", 0)
            .set("NewBillGenericTaxTotal", 0);

        let selectedSite: SitesModel = null;
        if (cacheStore.SiteCache.length > 1) {
            const a: SiteUIModel[] = [];
            for (let cachedSite of cacheStore.SiteCache) {
                for (let siteId of sessionStore.getCurrentEmployee().Model.AssociatedSites) {
                    if (siteId === cachedSite.Model.Id) {
                        const site = SiteUIModel.FromCompositeObject(cachedSite);
                        a.push(site);
                    }
                }
            }
            const sites = Immutable.List(a);
            privateBillModel = privateBillModel.set("Sites", sites);

            if (!IsNullOrWhiteSpace(sessionStore.cachedPrivateBillSiteId)) {
                selectedSite = cacheStore.SiteCache.filter(s => s.Model.Id === sessionStore.cachedPrivateBillSiteId)[0];
                privateBillModel = privateBillModel
                    .set("SelectedSiteId", sessionStore.cachedPrivateBillSiteId)
                    .set("Clinicians", CommonHelper.GetEmployeesBasedOnSite(Immutable.List(cacheStore.GetClinicians(false)), a, sessionStore.cachedPrivateBillSiteId));
            }
            else {
                const currentSiteId = sessionStore.getCurrentEmployee().getActiveSite().Model.Id;
                selectedSite = cacheStore.SiteCache.filter(s => s.Model.Id === currentSiteId)[0];
                privateBillModel = privateBillModel
                    .set("SelectedSiteId", currentSiteId)
                    .set("Clinicians", CommonHelper.GetEmployeesBasedOnSite(Immutable.List(cacheStore.GetClinicians(false)), a, currentSiteId));
            }
        }
        else {
            privateBillModel = privateBillModel.set("Clinicians", Immutable.List(cacheStore.GetClinicians(false)));
            selectedSite = sessionStore.getCurrentEmployee().getActiveSite();
            privateBillModel = privateBillModel.set("SelectedSiteId", selectedSite.Model.Id);
        }

        if (sessionStore.getCurrentEmployee().isActiveSiteMexico() === true) {
            store.ActionsMedicalNote.LoadCostCenterForSite(selectedSite.Model.Id);
        }

        privateBillModel = privateBillModel
            .set("SelectedPaymentMethod", BillingPaymentMethod.Payable)
            .set("SelectedSiteId", selectedSite.Model.Id)
            .set("GSTPercent", selectedSite.Model.GstValue)
            .set("QSTPercent", selectedSite.Model.QstValue)
            .set("HSTPercent", selectedSite.Model.HstValue)
            .set("GenericTaxPercent", selectedSite.Model.GenericTaxValue);

        const newId = GuidHelper.NewGuid();
        const itemA: IBillableService = Immutable.fromJS({
            Id: newId,
            Qty: 1,
            SelectedService: null,
            Services: null,
            Price: 0.00,
            HasGST: false,
            HasQST: false,
            HasHST: false,
            HasGenericTax: false,
            IsFromServiceList: false
        });

        noteModel = noteModel
            .set("PrivateBillBillableServices", Immutable.List([itemA]))
            .set("PrivateBillPopupModel", privateBillModel)
            .set("IsPrivateBillEditPopupOpen", true);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        sessionStore.showGlobalOverlay(true);
    }

    private closePrivateBillPopup(request: LocalEvent, store: PatientStore): void {
        const billModel = Immutable.fromJS(PatientModelHelper.getDefaultPrivateBillModel());
        const noteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("IsPrivateBillPopupOpen", false)
            .set("IsPrivateBillCreationWarningPopupOpen", false)
            .set("PrivateBillPopupModel", billModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        sessionStore.hideGlobalOverlay();
    }

    private closePrivateBillEditPopup(request: LocalEvent, store: PatientStore): void {
        const billModel = Immutable.fromJS(PatientModelHelper.getDefaultPrivateBillModel());
        const noteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("IsPrivateBillEditPopupOpen", false)
            .set("IsPrivateBillCreationWarningPopupOpen", false)
            .set("PrivateBillPopupModel", billModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        sessionStore.hideGlobalOverlay();
    }

    private loadDummyServices(request: LocalEvent, store: PatientStore): void {
        const newId = GuidHelper.NewGuid();
        const itemA: IBillableService = Immutable.fromJS({
            Id: newId,
            Qty: 1,
            SelectedService: null,
            Services: null,
            Price: 0.00,
            HasGST: false,
            HasQST: false,
            HasHST: false,
            HasGenericTax: false,
            IsFromServiceList: false
        });

        const noteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("PrivateBillBillableServices", Immutable.List([itemA]));
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private onTodayClick(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const privateBillModel = noteModel.get("PrivateBillPopupModel")
            .set("SelectedDateOfVisit", new Date());

        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private dateOfVisitChange(request: LocalEvent, store: PatientStore): void {
        const date = request.Get<Date>("value");
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        const privateBillModel = noteModel.get("PrivateBillPopupModel")
            .set("SelectedDateOfVisit", date);
        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private onSiteChanged(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let privateBillModel = noteModel.get("PrivateBillPopupModel");

        const site = cacheStore.GetSiteById(request.Get<string>("value"));
        const allSites = privateBillModel.get("Sites").toArray();

        privateBillModel = privateBillModel
            .set("SelectedSiteId", site.Model.Id)
            .set("Clinicians", CommonHelper.GetEmployeesBasedOnSite(Immutable.List(cacheStore.GetClinicians(false)), allSites, site.Model.Id))
            .set("SelectedClinicianName", "")
            .set("SelectedClinicians", null)

            .set("GSTPercent", site.Model.GstValue)
            .set("QSTPercent", site.Model.QstValue)
            .set("HSTPercent", site.Model.HstValue)
            .set("GenericTaxPercent", site.Model.GenericTaxValue);

        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        if (sessionStore.getCurrentEmployee().isActiveSiteMexico() === true) {
            store.ActionsMedicalNote.LoadCostCenterForSite(site.Model.Id);
        }

        this.calculateSubtotalAndTotal(request.PatientId, store);
    }

    private addNewEmptyService(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        const billableService = noteModel.get("PrivateBillBillableServices").toArray();

        const item = {
            Id: GuidHelper.NewGuid(),
            Qty: 1,
            SelectedService: null,
            Services: null,
            Price: 0.00,
            HasGST: false,
            HasQST: false
        };

        const pushItem: IBillableService = Immutable.fromJS(item);
        billableService.push(pushItem);
        noteModel = noteModel.set("PrivateBillBillableServices", Immutable.List(billableService));
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private changeHasQst(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("id");
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const billableService = noteModel.get("PrivateBillBillableServices").toArray();

        for (let i = 0; i < billableService.length; i++) {
            const x = billableService[i];

            if (x.get("Id") === id) {
                const isChecked = x.get("HasQST");
                billableService[i] = billableService[i].set("HasQST", !isChecked);
            }
        }

        noteModel = noteModel.set("PrivateBillBillableServices", Immutable.List(billableService));
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        this.calculateSubtotalAndTotal(request.PatientId, store);
    }

    private changeHasGst(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("id");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const billableService = noteModel.get("PrivateBillBillableServices").toArray();

        for (let i = 0; i < billableService.length; i++) {
            const x = billableService[i];

            if (x.get("Id") === id) {
                const isChecked = x.get("HasGST");
                billableService[i] = billableService[i].set("HasGST", !isChecked);
            }
        }

        noteModel = noteModel.set("PrivateBillBillableServices", Immutable.List(billableService));
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        this.calculateSubtotalAndTotal(request.PatientId, store);
    }

    private changeHasHst(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("id");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const billableService = noteModel.get("PrivateBillBillableServices").toArray();

        for (let i = 0; i < billableService.length; i++) {
            const x = billableService[i];

            if (x.get("Id") === id) {
                const isChecked = x.get("HasHST");
                billableService[i] = billableService[i].set("HasHST", !isChecked);
            }
        }

        noteModel = noteModel.set("PrivateBillBillableServices", Immutable.List(billableService));
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        this.calculateSubtotalAndTotal(request.PatientId, store);
    }

    private changeHasGenericTax(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("id");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const billableService = noteModel.get("PrivateBillBillableServices").toArray();

        for (let i = 0; i < billableService.length; i++) {
            const x = billableService[i];

            if (x.get("Id") === id) {
                const isChecked = x.get("HasGenericTax");
                billableService[i] = billableService[i].set("HasGenericTax", !isChecked);
            }
        }

        noteModel = noteModel.set("PrivateBillBillableServices", Immutable.List(billableService));
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        this.calculateSubtotalAndTotal(request.PatientId, store);
    }

    private removeService(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let billableService = noteModel.get("PrivateBillBillableServices").toArray();

        if (billableService.length >= 1)
            billableService = billableService.filter(x => x.get("Id") !== request.Get<string>("id"));

        noteModel = noteModel.set("PrivateBillBillableServices", Immutable.List(billableService));
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        this.calculateSubtotalAndTotal(request.PatientId, store);
    }

    public static calculateSubtotalAndTotalStatic(patientId: string, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[patientId];
        let allServices = noteModel.get("PrivateBillBillableServices").toArray();
        let selectedSiteModel = cacheStore.GetSiteById(noteModel.get("PrivateBillPopupModel").get("SelectedSiteId"));

        let privateBillModel = noteModel.get("PrivateBillPopupModel")
            .set("NewBillTotal", 0)
            .set("NewBillSubtotal", 0)
            .set("NewBillTotalAmount", 0);

        let total = 0;
        let subtotal = 0;
        let totalAmountWithoutDiscount = 0;
        let totalGst = 0;
        let totalQst = 0;
        let totalHst = 0;
        let totalGenericTax = 0;

        let gstPercent = privateBillModel.get("GSTPercent") / 100;
        let qstPercent = privateBillModel.get("QSTPercent") / 100;
        let hstPercent = privateBillModel.get("HSTPercent") / 100;
        let genericTaxPercent = privateBillModel.get("GenericTaxPercent") / 100;
        if (isNaN(gstPercent) || IsNullOrUndefined(gstPercent))
            gstPercent = 0;
        if (isNaN(qstPercent) || IsNullOrUndefined(qstPercent))
            qstPercent = 0;
        if (isNaN(hstPercent) || IsNullOrUndefined(hstPercent))
            hstPercent = 0;
        if (isNaN(genericTaxPercent) || IsNullOrUndefined(genericTaxPercent))
            genericTaxPercent = 0;

        let cashDiscountType = privateBillModel.get("IsCashSelected") === true ? DiscountType.Cash : DiscountType.Percent;

        // Fix Bad Qtys
        allServices = allServices.map(s => {
            const qty = s.get("Qty");
            if (isNaN(qty) || IsNullOrUndefined(qty))
                s = s.set("Qty", 1);

            return s;
        });

        // Calculate totalAmountWithoutDiscount
        for (let s of allServices) {
            const servicePriceTotal = Number(s.get("Price")) * s.get("Qty");
            totalAmountWithoutDiscount += servicePriceTotal;
        }

        // Convert Cash Discount
        let discountPercentage = 0;
        if (cashDiscountType == DiscountType.Cash && privateBillModel.get("NewBillDiscount") > 0 && totalAmountWithoutDiscount > 0) {
            discountPercentage = privateBillModel.get("NewBillDiscount") / totalAmountWithoutDiscount;
            cashDiscountType = DiscountType.Percent;
        }
        else if (cashDiscountType == DiscountType.Percent && privateBillModel.get("NewBillDiscount") > 0)
            discountPercentage = privateBillModel.get("NewBillDiscount") / 100;

        // Set Tax and Apply Discount on taxable Item;
        allServices = allServices.map(s => {
            const servicePriceTotal = Number(s.get("Price")) * s.get("Qty");
            let serviceFinalPrice = servicePriceTotal;

            let gst = (s.get("HasGST") === true && selectedSiteModel.isGst()) ? servicePriceTotal * gstPercent : 0;
            let qst = (s.get("HasQST") === true && selectedSiteModel.isPstOrQst()) ? servicePriceTotal * qstPercent : 0;
            let hst = (s.get("HasHST") === true && selectedSiteModel.isHst()) ? servicePriceTotal * hstPercent : 0;
            let genericTax = (s.get("HasGenericTax") === true && !selectedSiteModel.isCanadaSite()) ? servicePriceTotal * genericTaxPercent : 0;
            let finalGst = gst;
            let finalQst = qst;
            let finalHst = hst;
            let finalGenericTax = genericTax;

            // Apply Discount
            if (discountPercentage > 0) {
                const percentageDiscounted = discountPercentage;
                finalGst -= gst * percentageDiscounted;
                finalQst -= qst * percentageDiscounted;
                finalHst -= hst * percentageDiscounted;
                finalGenericTax -= genericTax * percentageDiscounted;
                serviceFinalPrice -= serviceFinalPrice * percentageDiscounted;
            }

            subtotal += serviceFinalPrice;
            totalGst += finalGst;
            totalQst += finalQst;
            totalHst += finalHst;
            totalGenericTax += finalGenericTax;
            total += serviceFinalPrice + finalGst + finalQst + finalHst + finalGenericTax;

            return s
                .set("GST", gst)
                .set("QST", qst)
                .set("HST", hst)
                .set("GenericTax", genericTax);
        });

        // Round pennies....
        privateBillModel = privateBillModel
            .set("NewBillSubtotal", Math.round((subtotal + 0.00001) * 100) / 100)
            .set("NewBillTotalAmount", Math.round((totalAmountWithoutDiscount + 0.00001) * 100) / 100)
            .set("NewBillTotal", Math.round((total + 0.00001) * 100) / 100)
            .set("NewBillGSTTotal", Math.round((totalGst + 0.00001) * 100) / 100)
            .set("NewBillQSTTotal", Math.round((totalQst + 0.00001) * 100) / 100)
            .set("NewBillHSTTotal", Math.round((totalHst + 0.00001) * 100) / 100)
            .set("NewBillGenericTaxTotal", Math.round((totalGenericTax + 0.00001) * 100) / 100);

        store.PatientHistoryBillableServicesModels[patientId] = allServices;

        noteModel = noteModel
            .set("PrivateBillBillableServices", Immutable.List(allServices))
            .set("PrivateBillPopupModel", privateBillModel);
        store.PatientMedicalNoteModels[patientId] = noteModel;
    }

    private calculateSubtotalAndTotal(patientId: string, store: PatientStore): void {
        PatientMedicalNoteHandler.calculateSubtotalAndTotalStatic(patientId, store);
    }

    private onSelectedPaymentChange(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const privateBillModel = noteModel.get("PrivateBillPopupModel")
            .set("SelectedPaymentMethod", request.Get<BillingPaymentMethod>("value"));
        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private selectClinician(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let privateBillModel = noteModel.get("PrivateBillPopupModel");

        const selectedClinician = request.Get<PersonDropdownItem>("value");
        if (selectedClinician == null || selectedClinician.Item == null)
            return;

        privateBillModel = store.handlePrivateBillProviderSelectionChange(privateBillModel, selectedClinician.Item);
        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private removeSelectedClinician(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const privateBillModel = noteModel.get("PrivateBillPopupModel")
            .set("SelectedClinicianName", "")
            .set("SelectedClinicians", null);

        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private clearSelection(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const allServices = noteModel.get("PrivateBillBillableServices").toArray();

        const id = request.Get<string>("id");

        function indexOf(services: IBillableService[], id: string): number {
            for (let i = 0; i < services.length; i++) {
                if (services[i].get("Id") === id)
                    return i;
            }
            return -1;
        }

        const serviceIndex = indexOf(allServices, id);

        if (serviceIndex >= 0) {
            allServices[serviceIndex] = allServices[serviceIndex]
                .set("SelectedService", null)
                .set("HasGST", false)
                .set("HasQST", false)
                .set("HasHST", false)
                .set("HasGenericTax", false)
                .set("Price", 0);

            noteModel = noteModel.set("PrivateBillBillableServices", Immutable.List(allServices));
            store.PatientMedicalNoteModels[request.PatientId] = noteModel;

            this.calculateSubtotalAndTotal(request.PatientId, store);

            store.EmitChangeToListeners();
        }
    }

    private selectBillableService(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const allServices = noteModel.get("PrivateBillBillableServices").toArray();

        delete store.PatientHistoryBillableServicesModels[request.PatientId];

        const item = request.Get<BillableServiceDropdownItem>("value");
        const id = request.Get<string>("id");

        function indexOf(services: IBillableService[], id: string): number {
            for (let i = 0; i < services.length; i++) {
                if (services[i].get("Id") === id)
                    return i;
            }
            return -1;
        }

        const serviceIndex = indexOf(allServices, id);

        if (serviceIndex >= 0) {
            allServices[serviceIndex] = allServices[serviceIndex]
                .set("SelectedService", item)
                .set("HasGST", item.Service.HasGST)
                .set("HasQST", item.Service.HasQST)
                .set("HasHST", item.Service.HasHST)
                .set("HasGenericTax", item.Service.HasGenericTax)
                .set("Price", item.Service.Price)
                .set("ServiceNumber", item.Service.Number)
                .set("NameFr", item.Service.NameFr)
                .set("NameEn", item.Service.NameEn)
                .set("NameEs", item.Service.NameEs)
                .set("CostCenterId", item.Service.CostCenterId)
                .set("IsFromServiceList", item.Service.IsFromServiceList);

            if (allServices[allServices.length - 1].get("SelectedService") !== null) {
                const emptyItem = {
                    Id: GuidHelper.NewGuid(),
                    Qty: 1,
                    SelectedService: null,
                    Services: null,
                    Price: 0.00,
                    HasGST: false,
                    HasQST: false,
                    HasHST: false,
                    HasGenericTax: false,
                    IsFromServiceList: false
                };

                const pushItem: IBillableService = Immutable.fromJS(emptyItem);
                allServices.push(pushItem);

                noteModel = noteModel.set("PrivateBillBillableServices", Immutable.List(allServices));
                store.PatientMedicalNoteModels[request.PatientId] = noteModel;
            }
            this.calculateSubtotalAndTotal(request.PatientId, store);

            store.EmitChangeToListeners();
        }
    }

    private onPriceChange(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("id");
        const value = request.Get<number>("value");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const billableService = noteModel.get("PrivateBillBillableServices").toArray();

        for (let i = 0; i < billableService.length; i++) {
            const x = billableService[i];

            if (x.get("Id") === id) {
                billableService[i] = billableService[i].set("Price", value);
            }
        }
        noteModel = noteModel.set("PrivateBillBillableServices", Immutable.List(billableService));
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        this.calculateSubtotalAndTotal(request.PatientId, store);
    }

    private onQuantityChange(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("id");
        const stringValue = request.Get<string>("value");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const billableService = noteModel.get("PrivateBillBillableServices").toArray();

        for (let i = 0; i < billableService.length; i++) {
            const x = billableService[i];

            if (x.get("Id") === id) {
                billableService[i] = billableService[i].set("Qty", Number(stringValue));
            }
        }

        noteModel = noteModel.set("PrivateBillBillableServices", Immutable.List(billableService));
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
        this.calculateSubtotalAndTotal(request.PatientId, store);
    }

    private changeDiscountType(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let privateBillModel = noteModel.get("PrivateBillPopupModel");

        const allServices = noteModel.get("PrivateBillBillableServices").toArray();

        const changeType = privateBillModel.get("IsCashSelected");

        privateBillModel = privateBillModel.set("IsCashSelected", !changeType);

        noteModel = noteModel
            .set("PrivateBillBillableServices", Immutable.List(allServices))
            .set("PrivateBillPopupModel", privateBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        this.calculateSubtotalAndTotal(request.PatientId, store);
    }

    private onDiscountChange(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let privateBillModel = noteModel.get("PrivateBillPopupModel");

        const allServices = noteModel.get("PrivateBillBillableServices").toArray();

        privateBillModel = privateBillModel.set("NewBillDiscount", request.Get<number>("value"));

        noteModel = noteModel
            .set("PrivateBillBillableServices", Immutable.List(allServices))
            .set("PrivateBillPopupModel", privateBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        this.calculateSubtotalAndTotal(request.PatientId, store);
    }

    private showSaveBillWarning(request: LocalEvent, store: PatientStore): void {
        const noteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("IsPrivateBillCreationWarningPopupOpen", true);
        sessionStore.showGlobalOverlay(true);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private printPrivateBill(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const privateBillModel = noteModel.get("PrivateBillPopupModel");

        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
        store.ActionsMedicalNote.PrintPrivateBill(privateBillModel.get("PrivateBillId"), true);

        noteModel = noteModel.set("IsPrivateBillCreationWarningPopupOpen", false);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private savePrivateBill(request: LocalEvent, store: PatientStore): void {
        const isClosingBill = request.Get<boolean>("IsFromClose");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let privateBillModel = noteModel.get("PrivateBillPopupModel");
        let billedServices = noteModel.get("PrivateBillBillableServices").toArray();

        const createdById = sessionStore.getCurrentEmployee().ContactFacetId;
        privateBillModel = privateBillModel.set("IsSave", true);

        sessionStore.cachedPrivateBillSiteId = privateBillModel.get("SelectedSiteId");

        if (privateBillModel.get("CostCenterList") && privateBillModel.get("CostCenterList").count() > 0) {
            sessionStore.cachedPrivateBillCostCenterId = privateBillModel.get("SelectedCostCenterId");
        }
        else {
            sessionStore.cachedPrivateBillCostCenterId = "";
        }

        noteModel = noteModel
            .set("PrivateBillPopupModel", privateBillModel)
            .set("IsPrivateBillSaveLocked", true);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        // Remove Empty Line
        billedServices = billedServices.filter(x =>
            !IsNullOrWhiteSpace(x.get("NameFr"))
            || !IsNullOrWhiteSpace(x.get("NameEn"))
            || !IsNullOrWhiteSpace(x.get("NameEs"))
            || x.get("IsFromServiceList") == true
            || (!isNaN(x.get("Price")) && x.get("Price") != 0)
        );

        store.ActionsMedicalNote.SavePrivateBill(privateBillModel, billedServices, store.PatientModels[request.PatientId].InactivableResponse.Id, createdById, noteModel.get("Encounter").Id, isClosingBill);
    }

    private billingNotesChange(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const privateBillModel = noteModel.get("PrivateBillPopupModel")
            .set("NewBillNotes", request.Get<string>("value"));

        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private saveInvalidation(request: LocalEvent, store: PatientStore): void {
        const noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const privateBillModel = noteModel.get("PrivateBillPopupModel");
        store.ActionsMedicalNote.InvalidatePrivateBill(privateBillModel.get("PrivateBillId"), noteModel.get("InvalidBillNote"));
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel;
    }

    private noteInvalidationChange(request: LocalEvent, store: PatientStore): void {
        const val = request.Get<string>("value");
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        const privateBillModel = noteModel.get("PrivateBillPopupModel")
            .set("InvalidationNoteForSave", val);

        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private closeInvalidPopup(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        const privateBillModel = noteModel.get("PrivateBillPopupModel")
            .set("IsInvalidPopupOpen", false);

        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private invalidateBill(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        noteModel = noteModel
            .set("InvalidBillNote", "")
            .set("ShowInvalidBillPopup", !noteModel.get("ShowInvalidBillPopup"));

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private onCostCenterChange(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        const privateBillModel = noteModel.get("PrivateBillPopupModel")
            .set("SelectedCostCenterId", request.Get<string>("value"));

        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private closePinPopup(request: LocalEvent, store: PatientStore): void {
        this.resetPrintingFaxing(store, request.PatientId);
    }

    private resetPrintingFaxing(store: PatientStore, patientId: string): void {
        const noteModel = store.PatientMedicalNoteModels[patientId]
            .set("IsConsultationRequestFaxPopupOpen", false)
            .set("PrintCurrentRequest", false)
            .set("SignCurrentRequest", false);
        store.PatientMedicalNoteModels[patientId] = noteModel;
    }

    private generatePrescriptionPdf(request: LocalEvent, store: PatientStore): void {
        const prescriptionId = request.Get<string>("prescriptionId");
        const withSignature = request.Get<boolean>("withSignature");
        const forceNonThermicPrintMode = request.Get<boolean>("ForceNonThermicPrintMode") || false;
        const pharmacy = request.Get<Pharmacy>("pharmacy");

        patientStore.ActionsMedicalNote.generatePrescriptionPdf(
            prescriptionId,
            withSignature,
            forceNonThermicPrintMode,
            pharmacy,
            fileName => patientStore.PatientMedicalNoteModels[request.PatientId] = store.PatientMedicalNoteModels[request.PatientId].set("PrintSignaturePreviewFileName", fileName)
        );
    }

    private sendPrescriptionFax(request: LocalEvent): void {
        const pharmacy = request.Get<Pharmacy>("pharmacy");
        const prescriptionId = request.Get<string>("prescriptionId");
        const setPreferred = request.Get<boolean>("setPreferred");
        const printCopy = request.Get<boolean>("printCopy");
        const withSignature = request.Get<boolean>("withSignature");

        patientStore.ActionsFax.SendPrescriptionFax(pharmacy, prescriptionId, setPreferred, printCopy, withSignature);
    }

    private closeMedfarBillingSpecialityPopup(request: LocalEvent, store: PatientStore): void {
        const noteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("SpecialtyMedfarBillingPopup", false);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private deleteMedfarBill(request: LocalEvent, store: PatientStore): void {
        const billId = request.Get<string>("billId");
        const encounterId = patientStore.PatientMedicalNoteModels[patientStore.getCurrentPatientContactId()].get("Encounter").Id;

        store.ActionsMedicalNote.DeleteMedfarBill(billId, encounterId);
    }

    private closeApprovalSupervisorPopup(request: LocalEvent, store: PatientStore): void {
        const noteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("ShowApprovalSupervisorPopup", false);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;

        sessionStore.hideGlobalOverlay();
    }

    private openSupervisorSelectionPopup(request: LocalEvent, store: PatientStore): void {
        const onOkCallback = request.Get<(supervisorContactId: string, message: string, allowListChanges: IAllowlistModel) => void>("onOkCallback");

        const noteModel = patientStore.PatientMedicalNoteModels[request.PatientId]
            .set("ShowApprovalSupervisorPopup", true)
            .set("ShowApprovalSupervisorPopupOkButtonCallBack", onOkCallback)
            .set("ShowApprovalSupervisorPopupOkButtonLabel", cacheStore.GetString("SendLabel"));

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
        sessionStore.showGlobalOverlay(true);
    }

    private generateMedicalNotePdf(request: LocalEvent): void {
        const encounterId = request.Get<string>("encounterId");
        const withSignature = request.Get<boolean>("withSignature");
        const patientContactId = request.Get<string>("patientContactId");
        const isPrintFormWithNote = request.Get<boolean>("isPrintFormsWithNote");

        patientStore.ActionsFax.GenerateMedicalNotePDF(patientContactId, encounterId, withSignature, isPrintFormWithNote);
    }

    private sendMedicalNoteFax(request: LocalEvent): void {
        const doctor = request.Get<MDClinician>("doctor");
        const encounterId = request.Get<string>("sendItemId");
        const pdfFileName = request.Get<string>("pdfFileName");
        const printCopy = request.Get<boolean>("printCopy");
        const patienId = request.PatientId;

        patientStore.ActionsFax.SendMedicalNoteFax(doctor, encounterId, patienId, pdfFileName);

        if (printCopy) {
            patientStore.ActionsMedicalNote.GenerateNotePDF(encounterId, true, request.PatientId, false);
        }
    }

    private closeConsultationRequest_ForFax(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];

        if (noteModel.get("IsConsultationRequestFaxPopupOpen")) {
            noteModel = noteModel.set("IsConsultationRequestFaxPopupOpen", false);
            sessionStore.hideGlobalOverlay();
        }
        if (noteModel.get("ConsultationRequestFaxResponse") != null) {
            noteModel.set("ConsultationRequestFaxResponse", null);
            sessionStore.hideGlobalOverlay();
        }

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }
    private sendConsultationRequestFax(request: LocalEvent, store: PatientStore): void {
        const consultationRequest = request.Get<ConsultationRequestsInboxItem>("consultationRequest");
        const pdf = request.Get<string>("pdf");
        const doctorName = request.Get<string>("doctorName");
        const doctorIdentifier = request.Get<string>("doctorIdentifier");
        const doctorAddress = request.Get<string>("doctorAddress");
        const doctorPhone = request.Get<string>("doctorPhone");
        const doctorFax = request.Get<string>("doctorFax");
        const syncToPortalAfterFax = request.Get<boolean>("syncToPortalAfterFax");
        const faxConsultationRequestComposite = new FaxConsultationRequestComposite();

        faxConsultationRequestComposite.ConsultationRequestId = consultationRequest.ConsultationRequestId;
        faxConsultationRequestComposite.PatientId = request.PatientId;
        faxConsultationRequestComposite.PdfFileName = pdf;
        faxConsultationRequestComposite.DoctorName = doctorName;
        faxConsultationRequestComposite.DoctorIdentifier = doctorIdentifier;
        faxConsultationRequestComposite.DoctorAddress = doctorAddress;
        faxConsultationRequestComposite.DoctorPhone = doctorPhone;
        faxConsultationRequestComposite.DoctorFax = doctorFax;

        clearTimeout(this.faxDebounceTimeoutReference);
        let then;
        if (syncToPortalAfterFax) {
            then = this.genericPortalPopupSet(
                request.PatientId,
                consultationRequest,
                pdf
            );
        }

        this.faxDebounceTimeoutReference = window.setTimeout(() => {
            store.ActionsMedicalNote.FaxConsultationRequest(faxConsultationRequestComposite, then);
        }, 250);
    }

    private AddConsultationRequestToNote(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<ConsultationRequestsInboxItem>("item");
        const patientContactId = request.Get<Guid>("patientContactId");
        const shouldSaveRelatedEmployee = request.Get<boolean>("shouldSaveRelatedEmployee");

        let patientNoteModel = patientStore.PatientMedicalNoteModels[patientContactId];
        let patientMedicalSummaryModel = patientStore.PatientMedicalSummaryModels[patientContactId];

        if (patientNoteModel) {
            const requests = patientNoteModel.get("ConsultationRequests").upsert(obj => obj.ConsultationRequestId == item.ConsultationRequestId, item);

            patientNoteModel = patientNoteModel.set("ConsultationRequests", requests);
            patientStore.PatientMedicalNoteModels[patientContactId] = patientNoteModel;
        }

        if (patientMedicalSummaryModel) {
            const requests = patientMedicalSummaryModel.get("ConsultationRequests")?.upsert(obj => obj.ConsultationRequestId == item.ConsultationRequestId, item);
            patientMedicalSummaryModel = patientMedicalSummaryModel.set("ConsultationRequests", requests);

            patientStore.PatientMedicalSummaryModels[patientContactId] = patientMedicalSummaryModel;
        }

        if (shouldSaveRelatedEmployee)
            store.updateContributorsEncountersData(patientContactId, item.EncounterId);

        store.ActionsMedicalSummary.reloadFollowUpScreeningData();
    }

    private toggleConsultationIsInvalid(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("consultationRequestId");
        const isInvalid = request.Get<boolean>("isInvalid");
        const patientContactId = request.Get<Guid>("patientContactId");
        const encounterId = request.Get<Guid>("encounterId");
        const sharedItemId = request.Get<string>("sharedItemId");

        let patientNoteModel = patientStore.PatientMedicalNoteModels[patientContactId];
        if (patientNoteModel) {
            let requests = patientNoteModel.get("ConsultationRequests");
            const consultationRequest = requests.toArray().firstOrDefault(x => x.ConsultationRequestId === id);
            if (consultationRequest) {
                consultationRequest.IsInvalid = isInvalid;
                requests = requests.upsert(obj => obj.ConsultationRequestId === id, consultationRequest);

                if (!isInvalid && !IsNullOrWhiteSpace(sharedItemId))
                    store.ActionsMedicalNote.reloadSharedItem(
                        sharedItemId,
                        sharedItem => consultationRequest.SharedItem = sharedItem
                    );
            }

            patientNoteModel = patientNoteModel.set("ConsultationRequests", requests);
        }
        patientStore.PatientMedicalNoteModels[patientContactId] = patientNoteModel;

        store.updateContributorsEncountersData(patientContactId, encounterId);

        store.ActionsMedicalSummary.reloadFollowUpScreeningData();
    }

    private toggleRequestIsInvalid(request: LocalEvent, store: PatientStore): void {
        const id = request.Get<string>("requestId");
        const isInvalid = request.Get<boolean>("isInvalid");
        const patientContactId = request.Get<Guid>("patientContactId");
        const encounterId = request.Get<Guid>("encounterId");
        const sharedItemId = request.Get<string>("sharedItemId");

        let patientNoteModel = patientStore.PatientMedicalNoteModels[patientContactId];
        if (patientNoteModel) {
            let requests = patientNoteModel.get("Requests");
            const labRequest = requests.toArray().firstOrDefault(x => x.RequestId === id);
            if (labRequest) {
                labRequest.IsInvalid = isInvalid;
                requests = requests.upsert(obj => obj.RequestId === id, labRequest);

                if (!isInvalid && !IsNullOrWhiteSpace(sharedItemId))
                    store.ActionsMedicalNote.reloadSharedItem(
                        sharedItemId,
                        sharedItem => labRequest.SharedItem = sharedItem
                    );
            }

            patientNoteModel = patientNoteModel.set("Requests", requests);
        }
        patientStore.PatientMedicalNoteModels[patientContactId] = patientNoteModel;

        store.updateContributorsEncountersData(patientContactId, encounterId);

        store.ActionsMedicalSummary.reloadFollowUpScreeningData();
    }

    private AddLabRequestToNote(request: LocalEvent, store: PatientStore): void {
        const item = request.Get<RequestsInboxItem>("item");
        const patientContactId = request.Get<Guid>("patientContactId");
        const shouldSaveRelatedEmployee = request.Get<boolean>("shouldSaveRelatedEmployee");

        let patientNoteModel = patientStore.PatientMedicalNoteModels[patientContactId];
        let patientMedicalSummaryModel = patientStore.PatientMedicalSummaryModels[patientContactId];

        if (patientNoteModel) {
            const requests = patientNoteModel.get("Requests")?.upsert(obj => obj.RequestId == item.RequestId, item);

            patientNoteModel = patientNoteModel.set("Requests", requests);
            patientStore.PatientMedicalNoteModels[patientContactId] = patientNoteModel;
        }

        if (patientMedicalSummaryModel) {
            const requests = patientMedicalSummaryModel.get("Requests")?.upsert(obj => obj.RequestId == item.RequestId, item);
            patientMedicalSummaryModel = patientMedicalSummaryModel.set("Requests", requests);

            patientStore.PatientMedicalSummaryModels[patientContactId] = patientMedicalSummaryModel;
        }

        if (shouldSaveRelatedEmployee)
            store.updateContributorsEncountersData(patientContactId, item.EncounterId);

        store.ActionsMedicalSummary.reloadFollowUpScreeningData();
    }

    private showReusingBillPopup(request: LocalEvent, store: PatientStore): void {
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];

        if (patientNoteModel.get("ShowReusingPrivateBillCriteria"))
            store.ActionsMedicalNote.LoadPrivateBillList(store.PatientModels[request.PatientId].InactivableResponse.Id, patientNoteModel.get("ReusingPrivateBillSearchText"), patientNoteModel.get("ReusingPrivateBillCriteriaFrom"), patientNoteModel.get("ReusingPrivateBillCriteriaTo"), true);
        else
            store.ActionsMedicalNote.LoadPrivateBillList(store.PatientModels[request.PatientId].InactivableResponse.Id, patientNoteModel.get("ReusingPrivateBillSearchText"), null, null, true);
    }

    private showCreatedPrivateBillEditPopup(request: LocalEvent, store: PatientStore): void {
        const data = request.Get<LightPrivateBill>("privateBill");
        store.ActionsMedicalNote.LoadPrivateBillInfoForEdit(data.PrivateBillId);
    }

    private closeReusingBillPopup(request: LocalEvent, store: PatientStore): void {
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("ShowReusingBillPopup", false);
        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;
        sessionStore.hideGlobalOverlay();
    }

    private showReusingPrivateBillCriteria(request: LocalEvent, store: PatientStore): void {
        let patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];

        const open = patientNoteModel.get("ShowReusingPrivateBillCriteria");
        patientNoteModel = patientNoteModel.set("ShowReusingPrivateBillCriteria", !open);

        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;

        if (patientNoteModel.get("ShowReusingPrivateBillCriteria"))
            store.ActionsMedicalNote.LoadPrivateBillList(store.PatientModels[request.PatientId].InactivableResponse.Id, patientNoteModel.get("ReusingPrivateBillSearchText"), patientNoteModel.get("ReusingPrivateBillCriteriaFrom"), patientNoteModel.get("ReusingPrivateBillCriteriaTo"), true);
        else
            store.ActionsMedicalNote.LoadPrivateBillList(store.PatientModels[request.PatientId].InactivableResponse.Id, patientNoteModel.get("ReusingPrivateBillSearchText"), null, null, true);
    }

    private reusingPrivateBillFromDateChange(request: LocalEvent, store: PatientStore): void {
        let patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];

        patientNoteModel = patientNoteModel.set("ReusingPrivateBillCriteriaFrom", request.Get<Date>("value"));
        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;

        store.ActionsMedicalNote.LoadPrivateBillList(store.PatientModels[request.PatientId].InactivableResponse.Id, patientNoteModel.get("ReusingPrivateBillSearchText"), patientNoteModel.get("ReusingPrivateBillCriteriaFrom"), patientNoteModel.get("ReusingPrivateBillCriteriaTo"), true);
    }

    private reusingPrivateBillToDateChange(request: LocalEvent, store: PatientStore): void {
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("ReusingPrivateBillCriteriaTo", request.Get<Date>("value"));
        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;

        store.ActionsMedicalNote.LoadPrivateBillList(store.PatientModels[request.PatientId].InactivableResponse.Id, patientNoteModel.get("ReusingPrivateBillSearchText"), patientNoteModel.get("ReusingPrivateBillCriteriaFrom"), patientNoteModel.get("ReusingPrivateBillCriteriaTo"), true);
    }

    private reusingPrivateBillSearchText(request: LocalEvent, store: PatientStore): void {
        let patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];

        patientNoteModel = patientNoteModel.set("ReusingPrivateBillSearchText", request.Get<string>("value"));
        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;

        if (patientNoteModel.get("ShowReusingPrivateBillCriteria"))
            store.ActionsMedicalNote.LoadPrivateBillList(store.PatientModels[request.PatientId].InactivableResponse.Id, patientNoteModel.get("ReusingPrivateBillSearchText"), patientNoteModel.get("ReusingPrivateBillCriteriaFrom"), patientNoteModel.get("ReusingPrivateBillCriteriaTo"), true);
        else
            store.ActionsMedicalNote.LoadPrivateBillList(store.PatientModels[request.PatientId].InactivableResponse.Id, patientNoteModel.get("ReusingPrivateBillSearchText"), null, null, true);
    }

    private selectReusingPrivateBillClick(request: LocalEvent, store: PatientStore): void {
        const privateBillId = request.Get<string>("privateBillId");

        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("SelectedReusedPrivateBillId", privateBillId);
        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;

        store.ActionsMedicalNote.PrintPrivateBill(privateBillId, false);
    }

    private reusingContinueClick(request: LocalEvent, store: PatientStore): void {
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        store.ActionsMedicalNote.LoadPrivateBillInfoForReuse(patientNoteModel.get("SelectedReusedPrivateBillId"));
    }

    private lockProvider(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let privateBillModel = noteModel.get("PrivateBillPopupModel");

        privateBillModel = privateBillModel.set("IsProviderLocked", !privateBillModel.get("IsProviderLocked"));

        sessionStore.rememberPrivateBillProvider = privateBillModel.get("IsProviderLocked");
        sessionStore.privateBillProviderContactFacetId = privateBillModel.get("IsProviderLocked") ? privateBillModel.get("SelectedClinicians").toArray()[0].ContactFacetId : "";

        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private invalidNoteChange(request: LocalEvent, store: PatientStore): void {
        const noteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("InvalidBillNote", request.Get<string>("InvalidNote"));
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    // ---------------------------------------------------------------------------------------------------
    // ------------------------- Lab Request Fax & Print -------------------------------------------------
    // ---------------------------------------------------------------------------------------------------

    private sendLabRequestFax(request: LocalEvent, store: PatientStore): void {
        const requestItem = request.Get<RequestsInboxItem>("labRequestFaxModel");

        const pdfName = request.Get<string>("pdf");
        const labName = request.Get<string>("labName");
        const labAddress = request.Get<string>("labAddress");
        const labPhone = request.Get<string>("labPhone");
        const labFax = request.Get<string>("labFax");
        const syncToPortalAfterFax = request.Get<boolean>("syncToPortalAfterFax");

        const faxLabRequestComposite = new FaxLabRequestComposite({
            Id: requestItem.RequestId,
            PatientId: request.PatientId,
            FileName: pdfName,
            LabName: labName,
            LabFax: labFax,
            LabPhone: labPhone,
            LabAddress: labAddress
        });

        let then;
        if (syncToPortalAfterFax) {
            then = this.genericPortalPopupSet(
                request.PatientId,
                requestItem,
                pdfName
            );
        }

        clearTimeout(this.faxDebounceTimeoutReference);
        this.faxDebounceTimeoutReference = window.setTimeout(() => {
            store.ActionsMedicalNote.SendLabRequestFax(faxLabRequestComposite, then);
        }, 250);
    }

    private genericPortalPopupSet(patientId: string, request: IPortalShareableItem, pdfName: string): void {
        let patientNoteModel = patientStore.PatientMedicalNoteModels[patientId];
        patientStore.PatientMedicalNoteModels[patientId] = patientNoteModel
            .set("IsRequestPortalPopupOpen", true)
            .set(
                "PortalLinkedItemModel",
                patientNoteModel.get("PortalLinkedItemModel")
                    .set("LinkedItemModel", request)
                    .set("PatientContactId", patientId)
                    .set("DefaultSubject", request.getDefaultSubject())
                    .set("LinkedItemMessages", (request.getSharedItemMessages() || []).toImmutable())
                    .set("LinkedItemType", request.getPortalLinkedItemType())
                    .set("IsSending", false)
                    .set("SharedItem", request.getSharedItem())
            )
            .set("SelectedPortalRequestId", request.getPortalObjectId())
            .set("PortalFileName", pdfName)
            .set("UnsignedPortalFileName", pdfName)
            .set("PortalFileUrl", PrintHelper.GetPreviewPath(pdfName))
            .set("PortalSyncPopupType", request.getMyleObjectType())
            .set("SyncToPortalMode", PortalSyncMode.Sync);
    }

    private handleGenerateLabRequestOperation(request: LocalEvent): void {
        let faxLabRequestComposite = request.Get<IFaxLabRequestComposite>("faxLabRequestComposite");
        let generateFileContext = request.Get<GenerateFileContext>("generateFileContext");
        this.handleGenerateLabRequest(request.PatientId, generateFileContext, faxLabRequestComposite);
    }

    private handleGenerateLabRequest(patientContactId: string, generateFileContext: GenerateFileContext, faxLabRequestComposite: IFaxLabRequestComposite): void {
        this.generateLabRequestPdf(
            faxLabRequestComposite
            , generateFileContext
            , (fileName, withSignature) => {
                switch (generateFileContext) {
                    case GenerateFileContext.Print:
                        PrintHelper.ShowPrintPreviewFileName(fileName);
                        break;
                    case GenerateFileContext.PreviewForSignatureOnFax:
                        if (!withSignature) {
                            patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                                .set("UnsignedPortalFileName", fileName)
                                .set("UnsignedPortalFileUrl", PrintHelper.GetPreviewPath(fileName));
                        }

                        patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                            .set("LabRequestPreviewPDFURLFileName", fileName) /* Preview when faxing */
                            .set("LabRequestPreviewPDFURL", PrintHelper.GetPreviewPath(fileName));
                        break;
                    case GenerateFileContext.PreviewForSignatureOnPrint:
                        patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                            .set("PrintSignaturePreviewFileName", fileName); /* Preview when adding Singature before Print */
                        break;
                    case GenerateFileContext.PreviewPortalSync:
                        if (!withSignature) {
                            patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                                .set("UnsignedPortalFileName", fileName)
                                .set("UnsignedPortalFileUrl", PrintHelper.GetPreviewPath(fileName));
                        }

                        patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                            .set("PortalFileName", fileName)
                            .set("PortalFileUrl", PrintHelper.GetPreviewPath(fileName));
                        break;
                }
            }
        );
    }

    private generateLabRequestPdf(faxRequestComposite: IFaxLabRequestComposite, generateFileContext: GenerateFileContext, then: (fileName: string, withSignature: boolean) => void): void {
        patientStore
            .ActionsMedicalNote
            .PrintSelectedRequest(
                {
                    RequestId: faxRequestComposite.RequestId,
                    FaxLabRequestComposite: {
                        LabName: faxRequestComposite.LabName,
                        LabFax: faxRequestComposite.LabFax,
                        RequestId: faxRequestComposite.RequestId,
                        WithSignature: faxRequestComposite.WithSignature
                    },
                    MarkAsPrinted: [
                        GenerateFileContext.Print,
                        GenerateFileContext.PreviewForSignatureOnFax,
                        GenerateFileContext.PreviewForSignatureOnPrint,
                        GenerateFileContext.PreviewPortalSync
                    ]
                        .contains(generateFileContext),
                    AddSignature: faxRequestComposite.WithSignature
                },
                fileName => then && then(fileName, faxRequestComposite.WithSignature)
            );
    }

    private handleGenerateConsultationRequestOperation(request: LocalEvent): void {
        let faxConsultationRequestComposite = request.Get<IFaxConsultationRequestComposite>("faxConsultationRequestComposite");
        let generateFileContext = request.Get<GenerateFileContext>("generateFileContext");
        // "forFax" not cleaned up yet;
        this.handleGenerateConsultationRequest(
            request.PatientId
            , generateFileContext
            , faxConsultationRequestComposite
        );
    }

    private handleGenerateConsultationRequest(patientContactId: string, generateFileContext: GenerateFileContext, faxConsultationRequestComposite: IFaxConsultationRequestComposite): void {
        this.generateConsultationequestPdf(
            faxConsultationRequestComposite
            , generateFileContext
            , (fileName, withSignature) => {
                switch (generateFileContext) {
                    case GenerateFileContext.Print:
                        PrintHelper.ShowPrintPreviewFileName(fileName);
                        break;
                    case GenerateFileContext.PreviewForSignatureOnFax:
                        if (!withSignature) {
                            // We set the unsigned version of the PDF to the portal variable in case the user as checked the
                            // sync to portal checkbox so we don't have to re-generate it
                            patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                                .set("UnsignedPortalFileName", fileName)
                                .set("UnsignedPortalFileUrl", PrintHelper.GetPreviewPath(fileName));
                        }

                        patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                            .set("ConsultationRequestPreviewPDFFileName", fileName)
                            .set("ConsultationRequestPreviewPDFURL", PrintHelper.GetPreviewPath(fileName));
                        break;
                    case GenerateFileContext.PreviewForSignatureOnPrint:
                        patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                            .set("PrintSignaturePreviewFileName", fileName);
                        break;
                    case GenerateFileContext.PreviewPortalSync:
                        if (!withSignature) {
                            patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                                .set("UnsignedPortalFileName", fileName)
                                .set("UnsignedPortalFileUrl", PrintHelper.GetPreviewPath(fileName));
                        }

                        patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                            .set("PortalFileName", fileName)
                            .set("PortalFileUrl", PrintHelper.GetPreviewPath(fileName));
                        break;
                }
            }
        );
    }

    private generateConsultationequestPdf(faxConsultationRequestComposite: IFaxConsultationRequestComposite, generateFileContext: GenerateFileContext, then: (fileName: string, withSignature: boolean) => void): void {
        patientStore
            .ActionsMedicalNote
            .PrintConsultationRequest(
                {
                    RequestId: faxConsultationRequestComposite.RequestId,
                    WithSignature: faxConsultationRequestComposite.WithSignature,
                    MarkAsPrinted: [
                        GenerateFileContext.Print,
                        GenerateFileContext.PreviewForSignatureOnFax,
                        GenerateFileContext.PreviewForSignatureOnPrint,
                        GenerateFileContext.PreviewPortalSync
                    ]
                        .contains(generateFileContext),
                    MarkAsFaxed: generateFileContext == GenerateFileContext.PreviewForSignatureOnFax
                },
                fileName => then && then(fileName, faxConsultationRequestComposite.WithSignature)
            );
    }

    private openConsultationRequestSignPopup_ForFax(request: LocalEvent, store: PatientStore): void {
        const requestId = request.Get<string>("requestId");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        store.PatientMedicalNoteModels[request.PatientId] = noteModel
            .set("IsConsultationRequestFaxPopupOpen", true)
            .set("ConsultationRequestFaxModel", noteModel.get("ConsultationRequests").find(x => x.ConsultationRequestId === requestId))
            .set("ConsultationRequestFaxResponse", null)
            .set("ConsultationRequestPreviewPDFFileName", null)
            .set("ConsultationRequestPreviewPDFURL", null);

        return this.handleGenerateConsultationRequest(
            request.PatientId,
            sessionStore.signOnPrintAndFaxAccess() ? GenerateFileContext.PreviewForSignatureOnFax : GenerateFileContext.Print, // LabRequestPreviewPDFURLFileName is set in this handler
            {
                RequestId: requestId,
                WithSignature: false
            }
        );
    }

    private openLabrequestSignPopup_ForFax(request: LocalEvent, store: PatientStore): void {
        const requestId = request.Get<string>("requestId");

        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        store.PatientMedicalNoteModels[request.PatientId] = noteModel
            .set("IsLabFaxPopupOpen", true)
            .set("LabRequestFaxModel", noteModel.get("Requests").find(x => x.RequestId === requestId))
            .set("LabRequestPreviewPDFURLFileName", null)
            .set("LabRequestPreviewPDFURL", null);

        return this.handleGenerateLabRequest(
            request.PatientId,
            sessionStore.signOnPrintAccess ? GenerateFileContext.PreviewForSignatureOnFax : GenerateFileContext.Print, // LabRequestPreviewPDFURLFileName is set in this handler
            {
                RequestId: requestId,
                WithSignature: false,
                LabFax: "",
                LabName: ""
            }
        );
    }

    private closeLabRequestSignPopup_ForFax(request: LocalEvent, store: PatientStore): void {
        const noteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("IsLabFaxPopupOpen", false)
            .set("LabRequestPreviewPDFURLFileName", null)
            .set("LabRequestPreviewPDFURL", null);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private closeFaxLabRequestResponse(request: LocalEvent, store: PatientStore): void {
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId]
            .set("LabRequestFaxResponse", null)
            .set("LabRequestFaxModel", null)
            .set("LabRequestPreviewPDFURLFileName", null)
            .set("LabRequestPreviewPDFURL", null);
        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;
        sessionStore.hideGlobalOverlay();
    }

    private showUnapproveNoteWarningPopup(request: LocalEvent, store: PatientStore): void {
        const activePatientId = request.PatientId;
        const noteModel = store.PatientMedicalNoteModels[activePatientId]
            .set("ShowUnapproveNoteWarningPopup", true)
            .set("ShowDeleteNoteWarningPopup", false);
        store.PatientMedicalNoteModels[activePatientId] = noteModel;
    }

    private unapproveNoteOk(request: LocalEvent, store: PatientStore): void {
        const encounterId = request.Get<string>("encounterId");
        const patientId = request.Get<string>("patientId");

        if (!IsNullOrWhiteSpace(encounterId)) {
            store.ActionsMedicalNote.unapproveEncounter(encounterId, patientId);
        }
    }

    private unapproveNoteCancel(request: LocalEvent, store: PatientStore): void {
        const activePatientId = request.PatientId;
        const noteModel = store.PatientMedicalNoteModels[activePatientId]
            .set("ShowUnapproveNoteWarningPopup", false);
        sessionStore.hideGlobalOverlay();

        store.PatientMedicalNoteModels[activePatientId] = noteModel;
    }

    private showDeleteNoteWarningPopup(request: LocalEvent, store: PatientStore): void {
        const activePatientId = request.PatientId;
        let noteModel = store.PatientMedicalNoteModels[activePatientId];
        const encounterId = noteModel.get("Encounter").Id;

        store.ActionsMedicalNote.GetEncounterStatus(
            encounterId,
            "saveEditNote",
            request.PatientId,
            () => {
                noteModel = noteModel
                    .set("ShowDeleteNoteWarningPopup", true)
                    .set("ShowUnapproveNoteWarningPopup", false);
                store.PatientMedicalNoteModels[activePatientId] = noteModel;
            }
        );
    }

    private deleteNoteCancel(request: LocalEvent, store: PatientStore): void {
        const activePatientId = request.PatientId;

        const noteModel = store.PatientMedicalNoteModels[activePatientId]
            .set("ShowDeleteNoteWarningPopup", false);
        sessionStore.hideGlobalOverlay();

        store.PatientMedicalNoteModels[activePatientId] = noteModel;
    }

    private deleteNoteOk(request: LocalEvent, store: PatientStore): void {
        const encounterId = request.Get<string>("encounterId");
        const patientId = request.Get<string>("patientId");
        const reasonCode = request.Get<string>("reasonCode");
        const details = request.Get<string>("reasondetail");

        const noteModel = store.PatientMedicalNoteModels[request.PatientId];

        store.PatientMedicalNoteUndoModels[request.PatientId] = noteModel
            .set("DeleteDetails", details)
            .set("DeleteReasonCode", reasonCode)
            .set("ViewMode", noteModel.get("ViewMode"));

        if (!IsNullOrWhiteSpace(encounterId) && !IsNullOrWhiteSpace(patientId)) {
            this.cancelEditNote(request, store);
            store.ActionsMedicalNote.deleteEncounter(encounterId, patientId, reasonCode, details);
        }
    }

    private closeDeletedEncounterWarningPopup(request: LocalEvent, store: PatientStore): void {
        const activePatientId = request.PatientId;

        const patientUIModel = store.PatientUIModels[activePatientId]
            .set("ShowEncounterWasDeletedWarning", false);
        sessionStore.hideGlobalOverlay();

        store.PatientUIModels[activePatientId] = patientUIModel;
    }

    private loadAttachedCommunications(request: LocalEvent, store: PatientStore): void {
        const encounterId = request.Get<string>("encounterId");

        store.ActionsMedicalNote.loadAttachedCommunications(encounterId);
    }

    private showAttachedCommunicationsList(request: LocalEvent): void {
        const eventTarget = request.Get<EventTarget>("event");
        const eventY = request.Get<number>("eventY");
        const eventX = request.Get<number>("eventX");
        const attachedCommunications = request.Get<AttachedCommunication[]>("attachedCommunications");
        const context = request.Get<AttachedCommunicationType>("context");

        let medicalNoteModel = patientStore.PatientMedicalNoteModels[request.PatientId];

        $(eventTarget).closest("tr").addClass("active");
        let a = $(".ws-footer").position().top;
        a = a - (eventY);

        $("#attached_communications_encounter").css("visibility", "hidden");

        window.setTimeout(() => {
            $("#attached_communications_encounter").removeClass("displaced");
            const h = $("#attached_communications_encounter").height();

            if (h > a) {
                const t = $("#attached_communications_encounter").position().top;
                $("#attached_communications_encounter").css("top", (t - h) + "px");
                $("#attached_communications_encounter").addClass("displaced");
            }
            $("#attached_communications_encounter").css("visibility", "visible");
        }, 30);

        medicalNoteModel = medicalNoteModel
            .set("AttachedCommunicationsPopupList", attachedCommunications)
            .set("AttachedCommunicationsPopupY", eventY)
            .set("AttachedCommunicationsPopupX", eventX)
            .set("AttachedCommunicationsPopupOpen", true)
            .set("AttachedCommunicationsContext", context);

        patientStore.PatientMedicalNoteModels[request.PatientId] = medicalNoteModel;
    }

    private onContainerClick(request: LocalEvent, store: PatientStore): void {
        const currentPatientId = request.PatientId;
        if (currentPatientId == null) return;
        let medicalNoteModel = store.PatientMedicalNoteModels[currentPatientId];
        if (medicalNoteModel && medicalNoteModel.get("AttachedCommunicationsPopupOpen") === true) {
            medicalNoteModel = medicalNoteModel.set("AttachedCommunicationsPopupOpen", false);
            patientStore.PatientMedicalNoteModels[currentPatientId] = medicalNoteModel;
            $("table.myle-table tbody tr").removeClass("active");
            patientStore.EmitChangeToListeners();
        }
    }

    private closePortalPopup(request: LocalEvent, store: PatientStore): void {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        noteModel = noteModel.set("IsRequestPortalPopupOpen", false);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private async sendRequestToPortal(request: LocalEvent, store: PatientStore): Promise<void> {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let portalStatusModel = noteModel.get("PortalLinkedItemModel");

        let subject = request.Get<string>("subject");
        let messageText = request.Get<string>("messageText");
        let includeDocument = request.Get<boolean>("includeDocument");

        const priority = request.Get<SharedItemPriority>("priority");
        const canPatientReplyWithMessage = request.Get<boolean>("canPatientReplyWithMessage");

        let portalMessages: SharedItemMessage[] = [];
        if (portalStatusModel != null) {
            portalMessages = PatientHelper.getShareItemMessagesToRevokeOrToSendToPortal(portalStatusModel);
        }

        const hasMessage = !IsNullOrWhiteSpace(messageText);
        const sharedItemId = portalStatusModel.get("LinkedItemModel").getSharedItemId();

        // create new message if user has entered text in message textbox
        if (hasMessage) {
            portalMessages.push(SharedItemMessage.createNewSharedItemMessage(messageText, priority));
        }

        const patient = store.PatientModels[request.PatientId];

        const isPdfCopiedToPortal = await store.ActionsMedicalNote.copySignedPdfToPortal(
            noteModel.get("SelectedPortalRequestId"),
            noteModel.get("UnsignedPortalFileName"),
            noteModel.get("PortalFileName"),
            noteModel.get("PortalLinkedItemModel").get("LinkedItemType"),
            noteModel.get("Encounter")?.Id
        );

        if (!isPdfCopiedToPortal) {
            console.error(this.unableToShareError);
            sessionStore.showPopup(cacheStore.GetString("SyncingToPortalFailedPleaseTryAgainLabel"));
            return;
        }

        store.ActionsMedicalNote.sendRequestToPortal({
            ObjectId: noteModel.get("SelectedPortalRequestId"),
            SharedItemType: noteModel.get("PortalLinkedItemModel").get("LinkedItemType"),
            Subject: subject,
            EmployeeContactId: sessionStore.getCurrentEmployee().ContactId,
            PatientContactId: patient.IndividualResponse.Contact_id,
            CreatedByUserId: sessionStore.userId,
            IncludeLinkedItem: includeDocument,
            Messages: portalMessages,
            SharedItemId: sharedItemId,
            canPatientReplyWithMessage
        });

        portalStatusModel = portalStatusModel.set("IsSending", true);
        noteModel = noteModel.set("PortalLinkedItemModel", portalStatusModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private requestPortalOpen(request: LocalEvent): void {
        const portalShareableItem = request.Get<IPortalShareableItem>("portalShareableItem");
        const unsignedFileName = request.Get<string>("unsignedFileName");
        const signedFileName = request.Get<string>("signedFileName");
        const skipSignature = request.Get<boolean>("skipSignature");
        this.openSyncToPortalPopupAsync(
            request.PatientId,
            portalShareableItem,
            unsignedFileName,
            signedFileName,
            skipSignature
        );
    }

    private async openSyncToPortalPopupWithPreview(request: LocalEvent, store: PatientStore): Promise<void> {
        const portalShareableItem = request.Get<IPortalShareableItem>("portalShareableItem");
        const exportableFilePreview = request.Get<ExportableFile>("exportableFilePreview");
        const skipSignature = request.Get<boolean>("skipSignature");
        const shouldShowPreview = request.Get<boolean>("shouldShowPreview") ?? true;

        this.openSyncToPortalPopupAsync(
            request.PatientId,
            portalShareableItem,
            null,
            null,
            skipSignature,
            exportableFilePreview
        );

        if (shouldShowPreview && exportableFilePreview) {
            const [unsignedFile, signedFile] = await this.getUnsignedAndSignedFiles(exportableFilePreview);

            store.PatientMedicalNoteModels[request.PatientId] = this.setPortalPreviewUrls(
                store.PatientMedicalNoteModels[request.PatientId],
                unsignedFile,
                signedFile
            );
        }
    }

    public async openSyncToPortalPopupAsync(patientContactId: string, shareableItem: IPortalShareableItem, unsignedFileName?: string, signedFileName?: string, skipSignature: boolean = false, exportableFile: ExportableFile = null): Promise<void> {
        let patientNoteModel = patientStore.PatientMedicalNoteModels[patientContactId];
        const patientModel = patientStore.PatientModels[patientContactId];

        const revokedOrUnsent = [PortalSyncStatus.Unsent, PortalSyncStatus.Revoked].contains(shareableItem?.getSharedItemPortalStatus());

        const mode = (!signedFileName && revokedOrUnsent) || shareableItem.hasBeenModifiedSinceLastShared()
            ? skipSignature ? PortalSyncMode.SkipSignAndSync : PortalSyncMode.SignAndSync
            : PortalSyncMode.Sync;

        const fileToDisplay = signedFileName || unsignedFileName;

        const linkedItemType = shareableItem.getPortalLinkedItemType();

        if (!exportableFile && linkedItemType === PortalLinkedItemType.MedicalNoteForm) {
            exportableFile = RadPdfExportableFileHelper.fromPdfFormInboxItem(
                shareableItem as PdfFormInboxItem
            );
        }

        if (!exportableFile && linkedItemType === PortalLinkedItemType.ObstetricalFile) {
            const obstetricalFileModel = shareableItem as ObstetricalFileModel;

            exportableFile = new FileTypeObstetricalFile({
                contributionId: obstetricalFileModel.ContributionId,
                description: cacheStore.GetString("ObstetricalFileLabel"),
                date: LuxonDateTimeParser.parse(obstetricalFileModel.ContributionCreationDate).toFormat("yyyy-MM-dd")
            });
        }

        if (!exportableFile && linkedItemType === PortalLinkedItemType.WorkSafeBcForm) {
            const workSafeBcFileModel = shareableItem as WorkSafeBcFormLight;

            exportableFile = FileTypeWorkSafeBcForm.fromWorkSafeBcForm(
                workSafeBcFileModel.Id,
                workSafeBcFileModel.toHeaderString(),
                workSafeBcFileModel.CreatedDate
            );
        }

        patientStore.PatientMedicalNoteModels[patientContactId] = patientNoteModel
            .set("IsRequestPortalPopupOpen", true)
            .set("SelectedPortalRequestId", shareableItem.getPortalObjectId())
            .set("PortalSyncPopupType", shareableItem.getMyleObjectType())
            .set(
                "PortalLinkedItemModel",
                patientNoteModel.get("PortalLinkedItemModel")
                    .set("LinkedItemModel", shareableItem)
                    .set("PatientContactId", patientContactId)
                    .set("RegistrationStatus", patientModel.IndividualResponse.PortalSyncState)
                    .set("DefaultSubject", shareableItem.getSharedItem() ? shareableItem.getSharedItem().Subject : shareableItem.getDefaultSubject())
                    .set("LinkedItemMessages", (shareableItem.getSharedItemMessages() || []).toImmutable())
                    .set("LinkedItemType", linkedItemType)
                    .set("IsSending", false)
                    .set("SharedItem", shareableItem.getSharedItem())
            )
            .set("PortalFileName", fileToDisplay)
            .set("UnsignedPortalFileName", fileToDisplay)
            .set("PortalFileUrl", fileToDisplay && PrintHelper.GetPreviewPath(fileToDisplay))
            .set("SyncToPortalMode", mode)
            .set("PortalPreviewExportableFile", exportableFile);

        if (!unsignedFileName) {
            let consultationIncludeSignature: boolean = false;
            let examRequestIncludeSignature: boolean = false;

            if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
                const pinFrequencyModel = LoginPinFrequencyModel.fromResponseModel(sessionStore.getCurrentEmployee()?.Model?.LoginPinFrequency);
                const pinFrequencyToken = sharedItemProviderFactory.get(linkedItemType).getPinFrequencyToken(pinFrequencyModel);
                const isPinValid = await ExportableFileHelper.validatePinFrequencyToken(pinFrequencyToken);
                const userSettings: ExportableFilePopupSettingModel = ExportableFilePopupSettingModel.fromCacheModel(cacheStore.UserSettings.ExportableFilePopup);
                const isSignedAuthorizationEnabled = cacheStore.ClinicSettings.EnableSignedAuthorization;

                let requestModel: RequestsInboxItem | ConsultationRequestsInboxItem;
                let isSignedByCurrentUser = false;

                if (linkedItemType == PortalLinkedItemType.ConsultationRequest)
                    requestModel = shareableItem as ConsultationRequestsInboxItem;
                else if (linkedItemType == PortalLinkedItemType.LabRequest)
                    requestModel = shareableItem as RequestsInboxItem;

                if (requestModel && isSignedAuthorizationEnabled) {
                    const printAuthorization = SignedPrintAuthorization.getLast(requestModel.SignedPrintAuthorizations);
                    isSignedByCurrentUser = printAuthorization?.CreatedByEmployeeId === sessionStore.getCurrentEmployee().ContactFacetId;
                }

                consultationIncludeSignature = isSignedByCurrentUser || (!!isPinValid && (userSettings?.consultationRequest?.exportableFileSignatureSettings?.firstOrDefault(
                    p => p.actionType == ExportMode.SendToPortal
                )?.shouldSign ?? false));

                examRequestIncludeSignature = isSignedByCurrentUser || (!!isPinValid && (userSettings?.examRequest?.exportableFileSignatureSettings?.firstOrDefault(
                    p => p.actionType == ExportMode.SendToPortal
                )?.shouldSign ?? false));
            }

            /* Generate the file to fill PortalRequestFileName and PortalRequestUrl */
            switch (shareableItem.getMyleObjectType()) {
                case MyleObjectType.ConsultationRequest:
                    if (consultationIncludeSignature) {
                        this.generateUnsignedExportableFile(
                            patientContactId,
                            GenerateFileContext.PreviewPortalSync,
                            {
                                RequestId: shareableItem.getPortalObjectId(),
                                WithSignature: false
                            },
                            PortalLinkedItemType.ConsultationRequest
                        );
                    }

                    this.handleGenerateConsultationRequest(patientContactId, GenerateFileContext.PreviewPortalSync, {
                        RequestId: shareableItem.getPortalObjectId(),
                        WithSignature: consultationIncludeSignature
                    });
                    break;
                case MyleObjectType.ExamRequest:
                    if (examRequestIncludeSignature) {
                        this.generateUnsignedExportableFile(
                            patientContactId,
                            GenerateFileContext.PreviewPortalSync,
                            {
                                RequestId: shareableItem.getPortalObjectId(),
                                WithSignature: false
                            },
                            PortalLinkedItemType.LabRequest
                        );
                    }

                    this.handleGenerateLabRequest(patientContactId, GenerateFileContext.PreviewPortalSync, {
                        RequestId: shareableItem.getPortalObjectId(),
                        WithSignature: examRequestIncludeSignature,
                        LabFax: "",
                        LabName: ""
                    });
                    break;
            }
        }
    }

    private onPortalRequestMessageChecked(request: LocalEvent, store: PatientStore): void {
        var checkedItem = request.Get<SharedItemMessage>("item");

        if (checkedItem != null) {
            let patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
            let model = patientNoteModel.get("PortalLinkedItemModel");

            model = PatientHelper.handlePortalItemCheck(model, checkedItem);

            patientNoteModel = patientNoteModel.set("PortalLinkedItemModel", model);

            store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel;
        }
    }

    private onPortalStatusRevoke(request: LocalEvent, store: PatientStore): void {
        const noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const portalStatusModel = noteModel.get("PortalLinkedItemModel");
        const sharedItemId = portalStatusModel.get("LinkedItemModel").getSharedItemId();
        const then = request.Get<() => void>("then");

        if (!IsNullOrWhiteSpace(sharedItemId)) {
            store.ActionsMedicalNote.revokeSharedItem(
                {
                    SharedItemId: sharedItemId,
                    SharedItemMessageIds: PortalHelper.getSharedMessageIdsToRevoke(portalStatusModel),
                    UserId: sessionStore.userId,
                    IncludeLinkedItem: true,
                    SharedItemType: portalStatusModel.get("LinkedItemType"),
                    ObjectId: portalStatusModel.get("LinkedItemModel").getPortalObjectId()
                },
                then
            );
        }
    }

    private closeRequestPortalPopup(request: LocalEvent, store: PatientStore): void {
        store.PatientMedicalNoteModels[request.PatientId] = store.PatientMedicalNoteModels[request.PatientId]
            .set("IsRequestPortalPopupOpen", false);
    }

    private async sendMedicalFormToPortal(request: LocalEvent, store: PatientStore): Promise<void> {
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        let portalStatusModel = noteModel.get("PortalLinkedItemModel");

        let subject = request.Get<string>("subject");
        let messageText = request.Get<string>("messageText");
        let includeDocument = request.Get<boolean>("includeDocument");
        let previewExportableFile = request.Get<ExportableFile>("exportableFile");

        const priority = request.Get<SharedItemPriority>("priority");
        const canPatientReplyWithMessage = request.Get<boolean>("canPatientReplyWithMessage");

        let formType = request.Get<PortalLinkedItemType>("formType")
            ?? PortalLinkedItemType.MedicalNoteForm;

        let portalMessages: SharedItemMessage[] = [];
        if (portalStatusModel != null) {
            portalMessages = PatientHelper.getShareItemMessagesToRevokeOrToSendToPortal(portalStatusModel);
        }

        const hasMessage = !IsNullOrWhiteSpace(messageText);
        let sharedItem = portalStatusModel.get("LinkedItemModel");
        let sharedItemId = sharedItem.getSharedItemId();

        if (hasMessage) {
            portalMessages.push(SharedItemMessage.createNewSharedItemMessage(messageText, priority));
        }

        let patient = store.PatientModels[request.PatientId];

        if (previewExportableFile) {
            const [unsignedFile, signedFile] = await this.getUnsignedAndSignedFiles(previewExportableFile);

            store.PatientMedicalNoteModels[request.PatientId] = this.setPortalPreviewUrls(
                store.PatientMedicalNoteModels[request.PatientId],
                unsignedFile,
                signedFile
            );
        }

        const encounterId = noteModel.get("Encounter")?.Id;

        let encounterFormDto = null;
        if (formType === PortalLinkedItemType.MedicalNoteForm) {
            if (!encounterId)
                throw Error("Encounter id must not be null when sending medical form to portal.");

            const patientFormId = noteModel.get("SelectedPortalRequestId");
            if (!patientFormId)
                throw Error("Patient form id must not be null when sending medical form to portal.");

            encounterFormDto = {
                PatientFormId: patientFormId,
                EncounterId: encounterId
            };
        }

        let updatedNoteModel = store.PatientMedicalNoteModels[request.PatientId];

        // always copy forms on portal on sync if not final cnesst pdf.
        if (previewExportableFile && IsNullOrWhiteSpace((previewExportableFile as FileTypeWorkerClaimCnesst).finalPdfId)) {
            const attachmentId = (previewExportableFile as FileTypeAttachment)?.AttachmentId;
            const isAttachment = previewExportableFile.fileType === FileType.ATTACHMENT && !!attachmentId;

            const isPdfCopiedToPortal = isAttachment
                ? await store.ActionsMedicalNote.copySignedAttachmentToPortal(
                    updatedNoteModel.get("SelectedPortalRequestId"),
                    attachmentId,
                    formType,
                    encounterId
                )
                : await store.ActionsMedicalNote.copySignedPdfToPortal(
                    updatedNoteModel.get("SelectedPortalRequestId"),
                    updatedNoteModel.get("UnsignedPortalFileName"),
                    updatedNoteModel.get("PortalFileName"),
                    formType,
                    encounterId
                );

            if (!isPdfCopiedToPortal) {
                console.error(this.unableToShareError);
                sessionStore.showPopup(cacheStore.GetString("SyncingToPortalFailedPleaseTryAgainLabel"));
                return;
            }
        }

        // sync to portal
        store.ActionsMedicalNote.sendMedicalFormToPortal({
            ObjectId: updatedNoteModel.get("SelectedPortalRequestId"),
            SharedItemType: updatedNoteModel.get("PortalLinkedItemModel").get("LinkedItemType"),
            Subject: subject,
            EmployeeContactId: sessionStore.getCurrentEmployee().ContactId,
            PatientContactId: patient.IndividualResponse.Contact_id,
            CreatedByUserId: sessionStore.userId,
            IncludeLinkedItem: includeDocument,
            Messages: portalMessages,
            SharedItemId: sharedItemId,
            EncounterFormDto: encounterFormDto,
            LinkedItemUpdated: includeDocument && sharedItem.hasBeenModifiedSinceLastShared(),
            CanPatientReplyWithMessage: canPatientReplyWithMessage
        });

        portalStatusModel = portalStatusModel.set("IsSending", true);
        updatedNoteModel = updatedNoteModel.set("PortalLinkedItemModel", portalStatusModel);

        store.PatientMedicalNoteModels[request.PatientId] = updatedNoteModel;
    }

    private async getUnsignedAndSignedFiles(exportableFile: ExportableFile): Promise<[IExportableFileLoaded, IExportableFileLoaded]> {
        if (!exportableFile)
            return;

        return await Promise.all([
            exportableFile.getFetchUrlDtoAsync(false),
            exportableFile.getFetchUrlDtoAsync(true)
        ]);
    }

    private setPortalPreviewUrls(
        noteModel: IPatientMedicalNoteModel,
        unsignedFile: IExportableFileLoaded,
        signedFile: IExportableFileLoaded
    ): IPatientMedicalNoteModel {
        return noteModel
            .set("UnsignedPortalFileName", MyleHelper.extractFileNameFromUrl(unsignedFile.FetchUrl))
            .set("UnsignedPortalFileUrl", unsignedFile.FetchUrl)
            .set("PortalFileName", MyleHelper.extractFileNameFromUrl(signedFile.FetchUrl))
            .set("PortalFileUrl", signedFile.FetchUrl);
    }

    private setFormToRevoke(request: LocalEvent): void {
        const portalShareableItem = request.Get<IPortalShareableItem>("portalShareableItem");
        const patientNoteModel = patientStore.PatientMedicalNoteModels[request.PatientId];

        patientStore.PatientMedicalNoteModels[request.PatientId] = patientNoteModel
            .set("PortalSyncPopupType", portalShareableItem.getMyleObjectType())
            .set("PortalLinkedItemModel", patientNoteModel
                .get("PortalLinkedItemModel")
                .set("LinkedItemModel", portalShareableItem)
                .set("PatientContactId", request.PatientId)
                .set("LinkedItemMessages", (portalShareableItem.getSharedItemMessages() || []).toImmutable())
                .set("LinkedItemType", portalShareableItem.getPortalLinkedItemType())
                .set("IsSending", false)
                .set("SharedItem", portalShareableItem.getSharedItem()));
    }

    private async updatePortalFormStatus(request: LocalEvent, store: PatientStore): Promise<void> {
        let encounterId = request.Get<string>("encounterId");
        store.ActionsMedicalNote.reloadEncounterForms(encounterId, store.PatientModels[request.PatientId].InactivableResponse.Id);
    }

    private updateCurrentEncounterExtensionFormsCount(request: LocalEvent, store: PatientStore): void {
        const patientContactId = request.Get<string>("PatientContactId");
        let noteModel = store.PatientMedicalNoteModels[patientContactId];
        if (!noteModel)
            return;

        const moduleName = request.Get<string>("ModuleName");
        const formsCount = request.Get<number>("FormsCount");

        noteModel = noteModel.set("FormsCountByModule", { ...noteModel.get("FormsCountByModule"), [moduleName]: formsCount });

        store.PatientMedicalNoteModels[patientContactId] = noteModel;
        store.EmitChangeToListeners();
    }

    private async updateSecondScreenExtensionFormsCount(request: LocalEvent, store: PatientStore): Promise<void> {
        const patientContactId = request.Get<string>("PatientContactId");
        let noteModels = store.PatientMedicalNotesCache[patientContactId];
        if (!noteModels)
            return;

        const moduleName = request.Get<string>("ModuleName");
        const formsCountByEncounterId = request.Get<{ [encounterId: string]: number }>("FormsCountByEncounterId");

        noteModels = noteModels.map(noteModel => noteModel.set("FormsCountByModule", { ...noteModel.get("FormsCountByModule"), [moduleName]: formsCountByEncounterId[noteModel.get("Encounter").Id] }));

        store.PatientMedicalNotesCache[patientContactId] = noteModels;
        store.EmitChangeToListeners();
    }

    private updateImmunizationMedicalItem(request: LocalEvent, store: PatientStore): void {
        const immunization = request.Get<Partial<MedicalItemImmunization>>("immunization");

        function updateImmunizationModel(immunizationList: Immutable.List<MedicalItem>): Immutable.List<MedicalItem> {
            const index = immunizationList.findIndex(x => x.Id === immunization.Id);
            if (index >= 0) {
                immunizationList = immunizationList
                    .set(index, new MedicalItemImmunization({ ...immunizationList.get(index), ...immunization }));
            }
            return immunizationList;
        }

        let patientMedicalItemsModels = store.PatientMedicalItemsModels[request.PatientId];
        if (patientMedicalItemsModels)
            patientMedicalItemsModels = patientMedicalItemsModels.set(
                "MedicalItemsList",
                updateImmunizationModel(patientMedicalItemsModels.get("MedicalItemsList"))
            );
        store.PatientMedicalItemsModels[request.PatientId] = patientMedicalItemsModels;

        let medicalSummaryModels = patientStore.PatientMedicalSummaryModels[request.PatientId];
        if (medicalSummaryModels)
            medicalSummaryModels = medicalSummaryModels.set(
                "MedicalItemsImmunization",
                updateImmunizationModel(medicalSummaryModels.get("MedicalItemsImmunization")) as Immutable.List<MedicalItemImmunization>
            );
        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModels;

        let medicalNoteModels = store.PatientMedicalNoteModels[request.PatientId];
        if (medicalNoteModels)
            medicalNoteModels = medicalNoteModels.set(
                "MedicalItemsImmunization",
                updateImmunizationModel(medicalNoteModels.get("MedicalItemsImmunization")) as Immutable.List<MedicalItemImmunization>
            );
        store.PatientMedicalNoteModels[request.PatientId] = medicalNoteModels;
        store.EmitChangeToListeners();
    }

    private updateImmunizationMedicalItemsAfterRvqSaveAndSync(request: LocalEvent, store: PatientStore): void {
        const updatedItems = request.Get<MedicalItemImmunization[]>("immunizations");
        const encounterId = request.Get<string>("encounterId");
        const emrTemplateId = request.Get<string>("emrTemplateId");

        let medicalSummaryModel = store.PatientMedicalSummaryModels[request.PatientId];
        let medicalNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        let patientMedicalItemsModels = store.PatientMedicalItemsModels[request.PatientId];

        if (patientMedicalItemsModels)
            patientMedicalItemsModels = patientMedicalItemsModels.set("SelectedEMRTemplateId", emrTemplateId);

        if (patientMedicalItemsModels) {
            patientMedicalItemsModels = patientMedicalItemsModels.set("AllMedicalItemsList", Immutable.List(updatedItems));

            let itemsToDisplay = PatientMedicalSummaryHelper.GetMedicalItemsToDisplay(patientMedicalItemsModels.get("AllMedicalItemsList"), emrTemplateId, encounterId);
            patientMedicalItemsModels = patientMedicalItemsModels.set("MedicalItemsList", Immutable.List(itemsToDisplay));
        }

        if (medicalSummaryModel)
            medicalSummaryModel = medicalSummaryModel.set(
                "MedicalItemsImmunization",
                updatedItems.map(x => MyleHelper.CloneObject(x)).toImmutable()
            );

        if (medicalNoteModel)
            medicalNoteModel = medicalNoteModel.set(
                "MedicalItemsImmunization",
                updatedItems.map(x => MyleHelper.CloneObject(x)).toImmutable()
            );

        store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
        store.PatientMedicalNoteModels[request.PatientId] = medicalNoteModel;
        store.PatientMedicalItemsModels[request.PatientId] = patientMedicalItemsModels;
    }

    private async onSubmitImmunizationToRvq(request: LocalEvent, store: PatientStore): Promise<void> {
        let immunizationMedicalItem = request.Get<MedicalItemImmunization>("immunizationMedicalItem");
        let updatedMedicalItems = request.Get<MedicalItemImmunization[]>("updatedMedicalItems");
        let emrTemplateId = request.Get<string>("emrTemplateId");

        if (!immunizationMedicalItem)
            return;

        let medicalItems = [immunizationMedicalItem];

        const response = await this.submitImmunizationToRvq(
            immunizationMedicalItem.Id,
            medicalItems
        );
        if (response.isSuccess) {
            immunizationMedicalItem.QuebecRegistryId = response.data?.Immunization?.Id ?? immunizationMedicalItem.QuebecRegistryId;
            immunizationMedicalItem.SubmittedToQuebecRegistryOn = response.data?.SubmittedToQuebecRegistryOn;
            request.AddItem("immunization", immunizationMedicalItem);

            let medicalItemsModel = patientStore.PatientMedicalItemsModels[request.PatientId];

            if (response.data?.PartiallyFailedErrorMessage?.any()) {
                sessionStore.showPopup(
                    [
                        cacheStore.GetString("QuebecVaccinationRegistry_FailedAntigensForRefusalVaccinationLabel"),
                        ...(response.data.PartiallyFailedErrorMessage || [])
                    ]
                );
                let updatedMedicalItemImmunization: MedicalItemImmunization;
                updatedMedicalItems = response.data?.UpdatedMedicalItems;
                if (updatedMedicalItems?.any()) {
                    updatedMedicalItemImmunization = updatedMedicalItems.firstOrDefault(x => x.Id === immunizationMedicalItem.Id);
                    updatedMedicalItemImmunization.SubmittedToQuebecRegistryOn = response.data?.SubmittedToQuebecRegistryOn;
                    request.AddItem("immunization", updatedMedicalItemImmunization);
                    let encounterId = updatedMedicalItems[0].RelatedEncounterId;
                    let medicalSummaryModel = patientStore.PatientMedicalSummaryModels[request.PatientId];
                    let medicalNoteModel = store.PatientMedicalNoteModels[request.PatientId];

                    if (medicalItemsModel) {
                        let updatedMedicalItems: MedicalItem[] = response.data?.UpdatedMedicalItems;
                        medicalItemsModel = medicalItemsModel.set("AllMedicalItemsList", Immutable.List(updatedMedicalItems));

                        let itemsToDisplay = PatientMedicalSummaryHelper.GetMedicalItemsToDisplay(medicalItemsModel.get("AllMedicalItemsList"), emrTemplateId, encounterId);
                        medicalItemsModel = medicalItemsModel.set("MedicalItemsList", Immutable.List(itemsToDisplay));
                    }

                    medicalSummaryModel = medicalSummaryModel.set(
                        "MedicalItemsImmunization",
                        updatedMedicalItems.map(x => MyleHelper.CloneObject(x)).toImmutable()
                    );
                    medicalNoteModel = medicalNoteModel.set(
                        "MedicalItemsImmunization",
                        updatedMedicalItems.map(x => MyleHelper.CloneObject(x)).toImmutable()
                    );

                    store.PatientMedicalSummaryModels[request.PatientId] = medicalSummaryModel;
                    store.PatientMedicalNoteModels[request.PatientId] = medicalNoteModel;
                }
            }
            medicalItemsModel = medicalItemsModel.set("IsSyncingToRvq", false);
            patientStore.PatientMedicalItemsModels[request.PatientId] = medicalItemsModel;

            this.updateImmunizationMedicalItem(request, store);
        }
        else {
            sessionStore.showPopup(response.errors);
        }
    }

    private async submitImmunizationToRvq(immunizationMedicalItemId: string, medicalItems: MedicalItemImmunization[]): Promise<ClientResponse<SyncRvqImmunizationResponse>> {
        return await Client.postAsync<SyncRvqImmunizationResponse>(
            ServerOperations.Server.QuebecVaccinationRegistry_SyncImmunization,
            {
                ImmunizationMedicalItemId: immunizationMedicalItemId,
                MedicalItems: medicalItems
            }
        );
    }

    private selectMedicalImmunizationFromRvq(request: LocalEvent, store: PatientStore): void {
        let immunizationModel = request.Get<ImmunizationModel>("immunizationModel");
        let medicalSummaryModel = patientStore.PatientMedicalSummaryModels[request.PatientId];
        let medicalItemModel = store.PatientMedicalItemsModels[request.PatientId];
        const allMedicalImmunizationItems = medicalSummaryModel.get("MedicalItemsImmunization");
        if (!allMedicalImmunizationItems || !immunizationModel)
            medicalItemModel = medicalItemModel.set("SelectedMedicalImmunizationFromRvq", null);
        else {
            medicalItemModel = medicalItemModel.set(
                "SelectedMedicalImmunizationFromRvq",
                allMedicalImmunizationItems
                    .toArray()
                    .firstOrDefault(x => x.QuebecRegistryId === immunizationModel.Id)
            );
        }

        store.PatientMedicalItemsModels[request.PatientId] = medicalItemModel;
    }

    private async reloadExternalBillingClaimsAsync(request: LocalEvent, store: PatientStore): Promise<void> {
        const encounterId = request.Get<Guid>("encounterId");
        const shouldFetchLatestStatusFromProvider = request.Get<boolean>("forceStatusUpdate") ?? false;

        if (!store.PatientMedicalNoteModels[request.PatientId])
            return;

        const noteEncounterId = store.PatientMedicalNoteModels[request.PatientId].get("Encounter").Id;
        if (noteEncounterId !== encounterId)
            return;

        const updatedClaims = await ExternalBillingManager.getClaimsByEncounterIdsAsync([encounterId], shouldFetchLatestStatusFromProvider);

        if (updatedClaims) {
            store.PatientMedicalNoteModels[request.PatientId] = store.PatientMedicalNoteModels[request.PatientId].set("ExternalBillingClaims", updatedClaims.toImmutable());
            store.EmitChangeToListeners();
        }
    }

    private onNewObstetricalFile(request: LocalEvent, store: PatientStore): void {
        const obstetricalFileType = request.Get<ObstetricalFileType>("obstetricalFileType");

        const noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounter = noteModel.get("Encounter");
        const patient = store.PatientModels[request.PatientId];
        const previousObstetricalFile = noteModel.get("PreviousObstetricalFiles").firstOrDefault(p => p.ObstetricalFileType === obstetricalFileType);
        const medicalSummary = patientStore.PatientMedicalSummaryModels[request.PatientId];
        const obstetricalSummary = medicalSummary.get("ObstetricalSummary");
        const vitalSigns = medicalSummary.get("MedicalItemsVitalSign")?.toArray() || [];
        const previousEncounters = medicalSummary.get("Encounters")
            .filter(x => LuxonDateTimeParser.parse(x.ConsultationDate) <= LuxonDateTimeParser.parse(encounter.ConsultationDate)).toArray();
        const screeningTests = medicalSummary.get("MedicalItemsScreeningTest").toArray();
        const latestPapTest = MedicalItemScreeningTest.getLastCompletedPapTest(screeningTests, previousEncounters);

        let form = ObstetricalFormCreatorFactory
            .forType(obstetricalFileType)
            ?.createObstetricalFileForm({
                patient,
                consultationDate: encounter.ConsultationDate,
                previousObstetricalFile,
                obstetricalCounters: obstetricalSummary ?? new ObstetricalCounters(),
                vitalSigns,
                latestPapTest,
                obstetricFormContext: new ObstetricFormContext({
                    PatientContactFacetId: patient.PatientResponse.ContactFacet_id,
                    EncounterId: encounter.Id
                })
            });

        if (!form) {
            sessionStore.showPopup(`Can't create obstetrical form for unsupported type '${obstetricalFileType}'.`);
            return;
        }

        store.ActionsMedicalNote.createNewObstetricalFile(encounter.Id, obstetricalFileType, form.stringify());
    }

    private onObstetricalFileContribution(request: LocalEvent, store: PatientStore): void {
        const encounterId = request.Get<string>("encounterId");
        const previousObstetricalFileContributionId = request.Get<string>("previousObstetricalFileContributionId");
        const obstetricalFileType = request.Get<ObstetricalFileType>("obstetricalFileType");
        const contributionType = request.Get<ObstetricalFileContributionType>("contributionType");

        store.ActionsMedicalNote.createObstetricalFileContribution(encounterId, previousObstetricalFileContributionId, obstetricalFileType, contributionType);
    }

    private onConfirmDeleteObstetricalFile(request: LocalEvent, store: PatientStore): void {
        let medicalNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        medicalNoteModel = medicalNoteModel.set("ShowDeleteObstetricalFileWarningPopup", false);

        store.PatientMedicalNoteModels[request.PatientId] = medicalNoteModel;
        store.ActionsMedicalNote.deleteObstetricalFile(request.Get<string>("encounterId"), request.Get<string>("obstetricalFileContributionId"));
    }

    private showDeleteObstetricalFileWarningPopup(request: LocalEvent, store: PatientStore): void {
        let medicalNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        medicalNoteModel = medicalNoteModel.set("ShowDeleteObstetricalFileWarningPopup", request.Get<boolean>("shouldShow"));

        store.PatientMedicalNoteModels[request.PatientId] = medicalNoteModel;
    }

    private updateObstetricalFileFormDisplay(request: LocalEvent, store: PatientStore): void {
        const displayModelChanges = request.Get<FormDisplay>("formDisplay");
        const noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const newFormDisplay = new FormDisplay(displayModelChanges);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel.set("ObstetricalFileFormDisplay", newFormDisplay);
    }

    private updateObstetricalFileForm(request: LocalEvent, store: PatientStore): void {
        const form = request.Get<string>("form");
        const emrTemplateId = request.Get<string>("emrTemplateId");
        const obstetricalCounters = request.Get<ObstetricalCounters>("obstetricalCounters");
        const modificationDate = request.Get<Date>("modificationDate");
        const patientContactFacetId = request.Get<string>("patientId");
        const patientContactId = store.resolvePatientContactId(patientContactFacetId);
        if (IsNullOrWhiteSpace(patientContactId))
            return; // patient closed

        const noteModel = store.PatientMedicalNoteModels[patientContactId];
        let summaryModel = store.PatientMedicalSummaryModels[patientContactId];

        if (noteModel) {
            let newObstetricalFileModel = noteModel.get("ObstetricalFileModel");
            newObstetricalFileModel.Form = form;
            newObstetricalFileModel.ContributionLastUpdateDate = modificationDate;

            noteModel.set("ObstetricalFileModel", newObstetricalFileModel);

            let obstetricalSummary = summaryModel?.get("ObstetricalSummary");
            if (newObstetricalFileModel.IsLatest && obstetricalSummary && obstetricalCounters) {
                obstetricalSummary.Gravida = obstetricalCounters.Gravida;
                obstetricalSummary.Term = obstetricalCounters.Term;
                obstetricalSummary.Premature = obstetricalCounters.Premature;
                obstetricalSummary.Abortion = obstetricalCounters.Abortion;
                obstetricalSummary.Live = obstetricalCounters.Live;
                obstetricalSummary.AbortionInduced = obstetricalCounters.AbortionInduced;
                obstetricalSummary.AbortionSpontaneous = obstetricalCounters.AbortionSpontaneous;
                obstetricalSummary.Stillbirth = obstetricalCounters.Stillbirth;
                obstetricalSummary.LastUpdateDate = modificationDate;
                obstetricalSummary.LastUpdatedBy = sessionStore.userId;

                summaryModel = summaryModel.set("ObstetricalSummary", obstetricalSummary);
            }

            if (emrTemplateId) {
                let vitalSigns = summaryModel.get("MedicalItemsVitalSign")?.toArray() || [];
                vitalSigns = vitalSigns.mutate(p => p.IsLinkedToObstetricalFile = true, p => p.EMRTemplateId === emrTemplateId);

                summaryModel = summaryModel.set("MedicalItemsVitalSign", Immutable.List(vitalSigns));
            }

            store.PatientMedicalSummaryModels[patientContactId] = summaryModel;
        }
    }

    private handleSaveWorkSafeBcForm(request: LocalEvent, store: PatientStore): void {
        const updatedForm = request.Get<WorkSafeBcFormLight>("form");
        const noteModel = store.PatientMedicalNoteModels[request.PatientId];

        noteModel.set(
            "WorkSafeBcForms",
            noteModel.get("WorkSafeBcForms").mutate(
                form => form.ModifiedOn = updatedForm.ModifiedOn,
                x => x.Id === updatedForm.Id
            )
        );

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private updateWorkSafeBcForms(request: LocalEvent, store: PatientStore): void {
        const forms = request.Get<WorkSafeBcFormLight[]>("forms");
        store.PatientMedicalNoteModels[request.PatientId] = store.PatientMedicalNoteModels[request.PatientId]
            .set("WorkSafeBcForms", forms);
    }

    private onRecipientDetailsChanged(request: LocalEvent, store: PatientStore): void {
        const recipientDetails = request.Get<RecipientDetailsModel>("recipientDetails");
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const privateBillModel = noteModel.get("PrivateBillPopupModel")
            .set("SelectedBillRecipientDetails", recipientDetails);

        noteModel = noteModel.set("PrivateBillPopupModel", privateBillModel);

        store.PatientMedicalNoteModels[request.PatientId] = noteModel;
    }

    private onBilledToChanged(request: LocalEvent, store: PatientStore): void {
        const billedTo = request.Get<PrivateBill_BilledTo>("value");
        let noteModel = store.PatientMedicalNoteModels[request.PatientId];
        const privateBillModel = noteModel.get("PrivateBillPopupModel").set("SelectedBillBilledTo", billedTo);
        store.PatientMedicalNoteModels[request.PatientId] = noteModel.set("PrivateBillPopupModel", privateBillModel);
    }

    private closePrivateBillPopups(request: LocalEvent, store: PatientStore): void {
        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        store.PatientMedicalNoteModels[request.PatientId] = patientNoteModel.set("IsPrivateBillEditPopupOpen", false).set("IsPrivateBillPopupOpen", false);
    }

    private beforeSaving(request: LocalEvent, store: PatientStore): void {
        const privateBillModel = store.PatientHistoryBillsModels[request.PatientId];
        sessionStore.cachedPrivateBillSiteId = privateBillModel.get("SelectedSiteId");
        store.PatientHistoryBillsModels[request.PatientId] = privateBillModel.set("IsSaveLocked", true);
    }

    private async saveAndPrintPrivateBill(request: LocalEvent, store: PatientStore): Promise<void> {
        this.beforeSaving(request, store);
        this.closePrivateBillPopups(request, store);

        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounterId = patientNoteModel.get("Encounter").Id;
        const privateBillModel = patientNoteModel.get("PrivateBillPopupModel");
        const billedServices = patientNoteModel.get("PrivateBillBillableServices").toArray();

        const response = await store.ActionsPrivateBill.saveAndPrintPrivateBillAsync(request, store, privateBillModel, billedServices, encounterId);

        if (!response?.isSuccess) {
            sessionStore.showPopup(response.errors);
        }
        else {
            PrintHelper.ShowPrintPreviewFileName(response.data?.FileName);
        }

        store.ActionsMedicalNote.LoadPrivateBillList(store.PatientUIModels[request.PatientId].get("PatientContactFacetId"), "", null, null, false, encounterId);
    }

    private async saveAndFaxPrivateBill(request: LocalEvent, store: PatientStore): Promise<void> {
        this.beforeSaving(request, store);
        this.closePrivateBillPopups(request, store);

        const patientNoteModel = store.PatientMedicalNoteModels[request.PatientId];
        const encounterId = patientNoteModel.get("Encounter").Id;
        const privateBillModel = patientNoteModel.get("PrivateBillPopupModel");
        const billedServices = patientNoteModel.get("PrivateBillBillableServices").toArray();

        const response = await store.ActionsPrivateBill.saveAndFaxPrivateBillAsync(request, store, privateBillModel, billedServices, encounterId);

        if (response?.isSuccess) {
            displayToast(MessageToast.Success, cacheStore.GetString("FaxSentLabel"));
        }
        else {
            sessionStore.showPopup(response.errors);
        }

        store.ActionsMedicalNote.LoadPrivateBillList(store.PatientUIModels[request.PatientId].get("PatientContactFacetId"), "", null, null, false, encounterId);
    }

    private isApssFormUnsent(form: PatientFormAPSS): boolean {
        return form.Status == null || ["Non verifiee", "A completer"].some(status => status === this.helper.RemoveDiacritics(form.Status?.toString()));
    }

    private incrementPatientBillingHistoryReload(request: LocalEvent, store: PatientStore): void {
        store.PatientUpdateBillingHistorySignal = store.PatientUpdateBillingHistorySignal + 1;
    }

    /**
     * Generate unsigned exam/consultation request on sync to portal
     * @param patientContactId: string
     * @param generateFileContext: GenerateFileContext
     * @param faxRequestComposite: IFaxConsultationRequestComposite | IFaxLabRequestComposite
     * @param linkType: PortalLinkedItemType
     */
    private generateUnsignedExportableFile(
        patientContactId: string,
        generateFileContext: GenerateFileContext,
        faxRequestComposite: IFaxConsultationRequestComposite | IFaxLabRequestComposite,
        linkType: PortalLinkedItemType
    ): void {
        if (linkType === PortalLinkedItemType.LabRequest) {
            this.generateLabRequestPdf(
                faxRequestComposite as IFaxLabRequestComposite,
                generateFileContext,
                async fileName => {
                    patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                        .set("UnsignedPortalFileName", fileName)
                        .set("UnsignedPortalFileUrl", PrintHelper.GetPreviewPath(fileName));
                }
            );
        }
        else {
            this.generateConsultationequestPdf(
                faxRequestComposite,
                generateFileContext,
                async fileName => {
                    patientStore.PatientMedicalNoteModels[patientContactId] = patientStore.PatientMedicalNoteModels[patientContactId]
                        .set("UnsignedPortalFileName", fileName)
                        .set("UnsignedPortalFileUrl", PrintHelper.GetPreviewPath(fileName));
                }
            );
        }
    }

    private enableHandWrittenNotesCache(request: LocalEvent, store: PatientStore): void {
        const noteModel = store.PatientMedicalNoteModels[request.PatientId];
        store.PatientMedicalNoteModels[request.PatientId] = noteModel.set("IsHandWrittenNoteModified", false);
    }

    private loadSentFaxes(request: LocalEvent, store: PatientStore): void {
        const sentFaxItemIds = request.Get<string[]>("SentFaxItemIds");
        store.ActionsMedicalNote.loadSentFaxes(sentFaxItemIds);
    }

    private updateProgressNote(request: LocalEvent, store: PatientStore): void {
        const patientContactId = request.Get<string>("PatientContactId");
        const encounterId = request.Get<string>("EncounterId");

        let noteModel = store.PatientMedicalNoteModels[patientContactId];
        if (noteModel?.get("Encounter").Id !== encounterId)
            return;

        let encounter = noteModel.get("Encounter");
        encounter.VisitPurpose = request.Get<string>("VisitPurpose");
        encounter.HistoricalNote = request.Get<string>("HistoricalNote");
        encounter.ProgressNote = request.Get<string>("ProgressNote");
        encounter.ExamenNote = request.Get<string>("ExamenNote");
        encounter.RadiologyNote = request.Get<string>("RadiologyNote");
        encounter.LabNote = request.Get<string>("LabNote");
        encounter.OtherPlanNote = request.Get<string>("OtherPlanNote");

        noteModel = noteModel.set("Encounter", encounter);

        store.PatientMedicalNoteModels[patientContactId] = noteModel;
    }

    private completeMedicalNoteLinkedAppointment(request: LocalEvent, store: PatientStore): void {
        const appointment = request.Get<PatientAppointmentItem>("appointment");

        store.ActionsMedicalNote.completeMedicalNoteLinkedAppointment(appointment);
    }
}

export { PatientMedicalNoteHandler };
